import React, { useState, useEffect, useCallback, useContext } from 'react';
import { X, Plus, Minus, Trash2, ChevronLeft, ChevronRight, Package, DollarSign, Tag, Pencil } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ImageUpload from '../../Components/seller/ImageUpload';

import { FaInfoCircle, FaPercent, FaTags, FaImage } from 'react-icons/fa';
import Dropdown from '../../Components/utils/Dropdown';
import countries from '../../data/countries.json';
import { states, calculateSellerAmount, formatCurrency, formatCurrencyDual, formatUsdToLocal, sellerFeePercentFor } from '../../data/constants';
import PayoutFeeNote from '../../Components/utils/PayoutFeeNote';
import axios from 'axios';
import { showToast } from "../../Components/utils/ShowToast";
import { CategoryContext } from '../../Contexts/CategoryContext';
import { LoginContext } from '../../Contexts/LoginContext';
import Loader from '../../Components/utils/Loader';

const DEFAULT_PACKAGES = [
    { name: 'Basic', subtitle: '', briefDescription: '', options: { timeToDeliver: '', timeframe: '', basePrice: 0, totalPrice: 0, offerings: [] } },
    { name: 'Standard', subtitle: '', briefDescription: '', options: { timeToDeliver: '', timeframe: '', basePrice: 0, totalPrice: 0, offerings: [] } },
    { name: 'Premium', subtitle: '', briefDescription: '', options: { timeToDeliver: '', timeframe: '', basePrice: 0, totalPrice: 0, offerings: [] } },
];

const STEP_LABELS = ['Product Details', 'Delivery & Policies', 'Pricing & Services'];
const B2B_STEP_LABELS = ['Listing Details', 'Pricing & Wholesale', 'Shipping & Services'];

const StepIndicator = ({ currentStep, labels = STEP_LABELS }) => (
    <div className="cpStepIndicator">
        {labels.map((label, index) => {
            const isCompleted = index < currentStep;
            const isCurrent = index === currentStep;
            return (
                <React.Fragment key={index}>
                    {index > 0 && <div className={`cpStepLine ${isCompleted ? 'completed' : ''}`} />}
                    <div className={`cpStep ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
                        <div className="cpStepCircle">
                            {isCompleted ? '✓' : index + 1}
                        </div>
                        <span className="cpStepLabel">{label}</span>
                    </div>
                </React.Fragment>
            );
        })}
    </div>
);

// ─── STEP 1: Product Details ─────────────────────────────────────────
const ProductDetailsStep = ({ formData, handleInputChange, errors, setFormData, sellerCountry, isService }) => {
    const { categories } = useContext(CategoryContext);
    const charCount = formData.productDescription.length;
    const selectedmaincategory = categories.find(cat => cat.name === formData.maincategory);
    const selectedsubcategory = selectedmaincategory?.subcategories?.find(sub => sub.name === formData.subcategory);
    const categoryScope = selectedmaincategory?.scope;
    const categoryType = selectedmaincategory?.type;
    const isCategoryService = categoryType === 'service';
    const isCanada = sellerCountry === 'Canada';
    const isFood = formData.maincategory === 'Food, Sweets & Drinks';

    const handleCategoryChange = (selected) => {
        const cat = categories.find(c => c.name === selected.value);
        setFormData(prev => ({
            ...prev,
            maincategory: selected.value,
            subcategory: '',
            childcategory: '',
            sellingScope: cat?.scope === 'local' ? 'local' : prev.sellingScope,
            serviceArea: 'country',
            serviceStates: [],
        }));
    };

    return (
        <div className="cpCard">
            <div className="cpCardHeader">
                <span className="cpCardIcon">📋</span>
                <div>
                    <h2 className="cpCardTitle">Product Details</h2>
                    <p className="cpCardSubtitle">Basic information about your product or service</p>
                </div>
            </div>
            <div className="cpCardBody">
                <div className="cpFormGrid">
                    <div className="cpFormGroup cpFormFull">
                        <label className="cpLabel">Product Title <span className="cpRequired">*</span></label>
                        <input
                            type="text"
                            className={`cpInput ${errors.productTitle ? 'cpInputError' : ''}`}
                            name="productTitle"
                            value={formData.productTitle}
                            onChange={handleInputChange}
                            placeholder="Enter a title e.g. '3-tier Cake'"
                        />
                        {errors.productTitle && <span className="cpError">{errors.productTitle}</span>}
                    </div>

                    <div className="cpFormGroup cpFormFull">
                        <label className="cpLabel">Product Description <span className="cpRequired">*</span></label>
                        <textarea
                            className={`cpInput cpTextarea ${errors.productDescription ? 'cpInputError' : ''}`}
                            name="productDescription"
                            value={formData.productDescription}
                            onChange={handleInputChange}
                            placeholder="Detailed description of your product"
                            maxLength={1200}
                            rows={4}
                        />
                        <div className="cpCharCount">{charCount} / 1200</div>
                        {errors.productDescription && <span className="cpError">{errors.productDescription}</span>}
                    </div>
                </div>

                <div className="cpDivider" />

                <h3 className="cpSectionLabel">Category</h3>
                <div className="cpFormGrid">
                    <div className="cpFormGroup">
                        <label className="cpLabel">Main Category <span className="cpRequired">*</span></label>
                        <Dropdown
                            options={categories.map(cat => ({ value: cat.name, label: cat.name }))}
                            value={formData.maincategory ? { value: formData.maincategory, label: formData.maincategory } : null}
                            onChange={handleCategoryChange}
                            placeholder="Select Main Category"
                            required
                        />
                        {errors.maincategory && <span className="cpError">{errors.maincategory}</span>}
                    </div>

                    {selectedmaincategory && (
                        <div className="cpFormGroup">
                            <label className="cpLabel">Sub Category <span className="cpRequired">*</span></label>
                            <Dropdown
                                options={selectedmaincategory.subcategories.map(sub => ({ value: sub.name, label: sub.name }))}
                                value={formData.subcategory ? { value: formData.subcategory, label: formData.subcategory } : null}
                                onChange={(selected) => setFormData(prev => ({ ...prev, subcategory: selected.value, childcategory: '' }))}
                                placeholder="Select Sub Category"
                                required
                            />
                            {errors.subcategory && <span className="cpError">{errors.subcategory}</span>}
                        </div>
                    )}

                    {selectedsubcategory && selectedsubcategory.children?.length > 0 && (
                        <div className="cpFormGroup">
                            <label className="cpLabel">Child Category</label>
                            <Dropdown
                                options={selectedsubcategory.children.map(child => ({ value: child.name, label: child.name }))}
                                value={formData.childcategory ? { value: formData.childcategory, label: formData.childcategory } : null}
                                onChange={(selected) => setFormData(prev => ({ ...prev, childcategory: selected.value }))}
                                placeholder="Select Child Category (Optional)"
                                isClearable
                            />
                        </div>
                    )}
                </div>

                {selectedmaincategory && (
                    <>
                        {categoryScope === 'local' && (
                            <div className="cpInfoBanner">
                                <FaInfoCircle />
                                <p>
                                    {isCategoryService
                                        ? 'Services in this category can only be offered locally.'
                                        : 'Products in this category can only be sold locally (within your country).'}
                                </p>
                            </div>
                        )}
                        {categoryScope === 'international' && (
                            <div className="cpFormGroup">
                                <label className="cpLabel">Selling Scope</label>
                                <Dropdown
                                    options={[
                                        { value: 'local', label: isCategoryService ? 'Within my country' : 'Sell Locally (within your country)' },
                                        { value: 'international', label: isCategoryService ? 'Not applicable' : 'Sell Internationally (worldwide)' },
                                    ].filter(opt => !(isCategoryService && opt.value === 'international'))}
                                    value={{ value: formData.sellingScope, label: formData.sellingScope === 'local' ? (isCategoryService ? 'Within my country' : 'Sell Locally') : 'Sell Internationally' }}
                                    onChange={(selected) => setFormData(prev => ({ ...prev, sellingScope: selected.value }))}
                                    placeholder="Select selling scope"
                                />
                            </div>
                        )}

                        {isCategoryService && (
                            <div className="cpFormGroup">
                                <label className="cpLabel">Service Area</label>
                                {isCanada ? (
                                    <>
                                        <Dropdown
                                            options={[
                                                { value: 'country', label: 'Throughout Canada' },
                                                { value: 'states', label: 'Specific Provinces Only' },
                                            ]}
                                            value={{ value: formData.serviceArea, label: formData.serviceArea === 'country' ? 'Throughout Canada' : 'Specific Provinces Only' }}
                                            onChange={(selected) => setFormData(prev => ({ ...prev, serviceArea: selected.value, serviceStates: selected.value === 'country' ? [] : prev.serviceStates }))}
                                            placeholder="Select service area"
                                        />
                                        {formData.serviceArea === 'states' && (
                                            <div className="cpStatesGrid">
                                                {states.map(state => (
                                                    <label key={state.name} className="cpCheckboxLabel">
                                                        <input
                                                            type="checkbox"
                                                            className="cpCheckbox"
                                                            checked={formData.serviceStates.includes(state.name)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setFormData(prev => ({ ...prev, serviceStates: [...prev.serviceStates, state.name] }));
                                                                } else {
                                                                    setFormData(prev => ({ ...prev, serviceStates: prev.serviceStates.filter(s => s !== state.name) }));
                                                                }
                                                            }}
                                                        />
                                                        {state.name}
                                                    </label>
                                                ))}
                                                {errors.serviceStates && <span className="cpError">{errors.serviceStates}</span>}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="cpInfoBanner">
                                        <FaInfoCircle />
                                        <p>Services in your country are offered throughout the country.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                <div className="cpDivider" />

                {isFood ? (
                    <>
                        <h3 className="cpSectionLabel">Food Details</h3>
                        <div className="cpFormGrid">
                            <div className="cpFormGroup">
                                <label className="cpLabel">Ingredients <span className="cpRequired">*</span></label>
                                <input
                                    type="text"
                                    className={`cpInput ${errors.ingredients ? 'cpInputError' : ''}`}
                                    name="ingredients"
                                    value={formData.ingredients}
                                    onChange={handleInputChange}
                                    placeholder="Enter the ingredients of your product"
                                />
                                {errors.ingredients && <span className="cpError">{errors.ingredients}</span>}
                            </div>
                            <div className="cpFormGroup">
                                <label className="cpLabel">Allergy Information</label>
                                <input
                                    type="text"
                                    className="cpInput"
                                    name="allergyInfo"
                                    value={formData.allergyInfo}
                                    onChange={handleInputChange}
                                    placeholder="Enter allergy information (if any)"
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <h3 className="cpSectionLabel">Product Specifications</h3>
                        <p className="cpHelpText">Add details like Size, Material, Weight, Color, etc.</p>
                        <div className="cpDetailRows">
                            {formData.productDetails?.map((detail, index) => (
                                <div key={index} className="cpDetailRow">
                                    <input
                                        type="text"
                                        className={`cpInput ${errors[`productDetailTitle_${index}`] ? 'cpInputError' : ''}`}
                                        placeholder="Title (e.g., Size, Material)"
                                        value={detail.title}
                                        onChange={(e) => {
                                            const newDetails = [...formData.productDetails];
                                            newDetails[index].title = e.target.value;
                                            setFormData(prev => ({ ...prev, productDetails: newDetails }));
                                        }}
                                    />
                                    <input
                                        type="text"
                                        className={`cpInput cpDetailDesc ${errors[`productDetailDesc_${index}`] ? 'cpInputError' : ''}`}
                                        placeholder="Description (e.g., XL, Cotton)"
                                        value={detail.description}
                                        onChange={(e) => {
                                            const newDetails = [...formData.productDetails];
                                            newDetails[index].description = e.target.value;
                                            setFormData(prev => ({ ...prev, productDetails: newDetails }));
                                        }}
                                    />
                                    {formData.productDetails.length > 1 && (
                                        <button
                                            type="button"
                                            className="cpRemoveBtn"
                                            onClick={() => {
                                                const newDetails = formData.productDetails.filter((_, i) => i !== index);
                                                setFormData(prev => ({ ...prev, productDetails: newDetails }));
                                            }}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                className="cpAddBtn"
                                onClick={() => {
                                    setFormData(prev => ({
                                        ...prev,
                                        productDetails: [...prev.productDetails, { title: '', description: '' }]
                                    }));
                                }}
                            >
                                <Plus size={16} /> Add Detail
                            </button>
                            {errors.productDetails && <span className="cpError">{errors.productDetails}</span>}
                        </div>
                    </>
                )}

                <div className="cpDivider" />

                {!isService && (
                    <>
                        <h3 className="cpSectionLabel">Stock & Inventory</h3>
                        <div className="cpFormGrid">
                            <div className="cpFormGroup">
                                <label className="cpLabel">Stock Quantity</label>
                                <input
                                    type="number"
                                    className="cpInput"
                                    name="stock"
                                    value={formData.stock === null ? '' : formData.stock}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setFormData(prev => ({ ...prev, stock: val === '' ? null : parseInt(val) }));
                                    }}
                                    placeholder="Leave empty for unlimited stock"
                                    min="0"
                                />
                                <span className="cpHelpText">Leave empty if stock is unlimited. When stock runs out, the listing will show as out of stock.</span>
                            </div>
                        </div>

                        <div className="cpDivider" />
                    </>
                )}

                <h3 className="cpSectionLabel">Product Images <span className="cpRequired">*</span></h3>
                <ImageUpload
                    onImagesChange={(imageData) => {
                        setFormData(prev => ({
                            ...prev,
                            images: imageData.images,
                            thumbnail: imageData.thumbnail
                        }));
                    }}
                    initialImages={formData.images}
                    initialThumbnail={formData.thumbnail}
                    key={formData.id || 'new'}
                />
                {errors.thumbnail && <span className="cpError">{errors.thumbnail}</span>}
            </div>
        </div>
    );
};

// ─── STEP 2: Delivery & Policies ─────────────────────────────────────
const DeliveryPoliciesStep = ({
    deliveryType, setDeliveryType, formData, setFormData, errors, handleInputChange,
    sellerAddress, accountZipCode, isService, currency, exchangeRate,
}) => {
    const isInternational = formData.sellingScope === 'international';

    const handleDeliveryTypeChange = (type) => {
        setDeliveryType((prev) => {
            const wasIncluded = prev.includes(type);
            const newTypes = wasIncluded ? prev.filter((t) => t !== type) : [...prev, type];
            if (type === 'pickup' && !wasIncluded && sellerAddress && !formData.pickupLocation?.address) {
                setFormData((prevForm) => ({
                    ...prevForm,
                    pickupLocation: {
                        country: sellerAddress.country || { name: '', code: '' },
                        state: sellerAddress.state || { name: '', tax: 0 },
                        city: sellerAddress.city || '',
                        address: sellerAddress.address || '',
                        zipCode: sellerAddress.zipCode || accountZipCode || '',
                    },
                }));
            }
            return newTypes;
        });
    };

    const handlePickupLocationChange = (field, value) => {
        if (field === 'country') {
            setFormData((prev) => ({
                ...prev,
                pickupLocation: {
                    ...prev.pickupLocation,
                    country: { name: value.label, code: value.value },
                    state: value.label === 'Canada' ? { name: '', tax: 0 } : { name: '', tax: 5 },
                },
            }));
        } else if (field === 'state' && formData.pickupLocation.country.name === 'Canada') {
            setFormData((prev) => ({
                ...prev,
                pickupLocation: { ...prev.pickupLocation, state: { name: value.label, tax: value.value } },
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                pickupLocation: { ...prev.pickupLocation, [field]: value },
            }));
        }
    };

    return (
        <>
            {/* Delivery Options */}
            <div className="cpCard">
                <div className="cpCardHeader">
                    <span className="cpCardIcon">🚚</span>
                    <div>
                        <h2 className="cpCardTitle">Delivery Options</h2>
                        <p className="cpCardSubtitle">How buyers will receive their order</p>
                    </div>
                </div>
                <div className="cpCardBody">
                    {isService ? (
                        <div className="cpFormGroup">
                            <label className="cpLabel">Service Mode <span className="cpRequired">*</span></label>
                            <Dropdown
                                options={[
                                    { value: 'online', label: 'Online / Digital Service' },
                                    { value: 'onsite', label: 'At Buyer Location (On-site)' },
                                    { value: 'at_seller_location', label: 'At Seller Location' },
                                ]}
                                value={formData.serviceMode ? {
                                    value: formData.serviceMode,
                                    label: formData.serviceMode === 'online'
                                        ? 'Online / Digital Service'
                                        : formData.serviceMode === 'onsite'
                                            ? 'At Buyer Location (On-site)'
                                            : 'At Seller Location'
                                } : null}
                                onChange={(selected) => setFormData(prev => ({ ...prev, serviceMode: selected.value }))}
                                placeholder="Select how service is delivered"
                            />
                            <span className="cpHelpText">
                                {formData.serviceMode === 'online' && 'The service will be delivered digitally/online. No physical address or delivery date needed from buyer.'}
                                {formData.serviceMode === 'onsite' && 'You will travel to the buyer\'s location to provide the service. Buyer address will be collected.'}
                                {formData.serviceMode === 'at_seller_location' && 'Buyer will visit your location. Your account location will be shown to buyers.'}
                                {!formData.serviceMode && 'Choose how you deliver your service.'}
                            </span>
                            {errors.serviceMode && <span className="cpError">{errors.serviceMode}</span>}
                        </div>
                    ) : (
                        <div className="cpFormGroup">
                            <label className="cpLabel">Select Delivery Options <span className="cpRequired">*</span></label>
                            <div className="cpDeliveryOptions">
                                <label className={`cpDeliveryOption ${deliveryType.includes('pickup') ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={deliveryType.includes('pickup')}
                                        onChange={() => handleDeliveryTypeChange('pickup')}
                                    />
                                    <div className="cpDeliveryOptionContent">
                                        <span className="cpDeliveryIcon">📍</span>
                                        <span className="cpDeliveryText">Pickup</span>
                                    </div>
                                </label>
                                <label className={`cpDeliveryOption ${deliveryType.includes('delivery') ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={deliveryType.includes('delivery')}
                                        onChange={() => handleDeliveryTypeChange('delivery')}
                                    />
                                    <div className="cpDeliveryOptionContent">
                                        <span className="cpDeliveryIcon">🚚</span>
                                        <span className="cpDeliveryText">Delivery</span>
                                    </div>
                                </label>
                            </div>
                            {errors.deliveryType && <span className="cpError">{errors.deliveryType}</span>}
                        </div>
                    )}

                    {deliveryType.includes('pickup') && !isService && (
                        <div className="cpPickupSection">
                            <h3 className="cpSectionLabel">Pickup Location</h3>
                            <div className="cpInfoBanner">
                                <FaInfoCircle />
                                <p>Your saved account address (Location &amp; Currency) is pre-filled below. Changes here apply only to this product.</p>
                            </div>
                            <div className="cpFormGrid">
                                <div className="cpFormGroup">
                                    <label className="cpLabel">Country</label>
                                    <Dropdown
                                        options={countries.map(c => ({ value: c.code, label: c.name }))}
                                        value={formData.pickupLocation.country.name ? { value: formData.pickupLocation.country.code, label: formData.pickupLocation.country.name } : null}
                                        onChange={(value) => handlePickupLocationChange('country', value)}
                                        placeholder="Select Country"
                                        required
                                    />
                                    {errors.pickupCountry && <span className="cpError">{errors.pickupCountry}</span>}
                                </div>
                                <div className="cpFormGroup">
                                    <label className="cpLabel">State / Province</label>
                                    {formData.pickupLocation.country.name === 'Canada' ? (
                                        <Dropdown
                                            options={states.map(s => ({ value: s.tax, label: s.name }))}
                                            value={formData.pickupLocation.state.name ? { value: formData.pickupLocation.state.tax, label: formData.pickupLocation.state.name } : null}
                                            onChange={(value) => handlePickupLocationChange('state', value)}
                                            placeholder="Select Province"
                                            required
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={formData.pickupLocation.state.name}
                                            onChange={(e) => handlePickupLocationChange('state', { name: e.target.value, tax: 5 })}
                                            className={`cpInput ${errors.pickupState ? 'cpInputError' : ''}`}
                                            placeholder="Enter state or province"
                                        />
                                    )}
                                    {errors.pickupState && <span className="cpError">{errors.pickupState}</span>}
                                </div>
                                <div className="cpFormGroup">
                                    <label className="cpLabel">City</label>
                                    <input
                                        type="text"
                                        value={formData.pickupLocation.city}
                                        onChange={(e) => handlePickupLocationChange('city', e.target.value)}
                                        className={`cpInput ${errors.pickupCity ? 'cpInputError' : ''}`}
                                        placeholder="Enter City"
                                    />
                                    {errors.pickupCity && <span className="cpError">{errors.pickupCity}</span>}
                                </div>
                                <div className="cpFormGroup">
                                    <label className="cpLabel">Full Address</label>
                                    <input
                                        type="text"
                                        value={formData.pickupLocation.address}
                                        onChange={(e) => handlePickupLocationChange('address', e.target.value)}
                                        className={`cpInput ${errors.pickupAddress ? 'cpInputError' : ''}`}
                                        placeholder="Enter full address"
                                    />
                                    {errors.pickupAddress && <span className="cpError">{errors.pickupAddress}</span>}
                                </div>
                                <div className="cpFormGroup">
                                    <label className="cpLabel">Postal / ZIP</label>
                                    <input
                                        type="text"
                                        value={formData.pickupLocation.zipCode || ''}
                                        onChange={(e) => handlePickupLocationChange('zipCode', e.target.value)}
                                        className="cpInput"
                                        placeholder="Postal or ZIP code"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {deliveryType.includes('delivery') && !isService && (
                        <div className="cpShippingSection">
                            <h3 className="cpSectionLabel">Shipping Policy</h3>
                            <div className="cpInfoBanner">
                                <FaInfoCircle />
                                <p>Choose how shipping fees are calculated for buyers' orders.</p>
                            </div>

                            {/* Shipping method selector */}
                            <div className="cpFormGroup">
                                <label className="cpLabel">Shipping Fee Calculation Method <span className="cpRequired">*</span></label>
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
                                    {[
                                        { value: 'byOrder', label: '📦 Flat Fee (per order)', desc: 'A single fixed fee regardless of quantity' },
                                        { value: 'byUnit', label: '📏 Per Unit', desc: 'Fee multiplied by the number of units ordered' },
                                        { value: 'byMOQ', label: '📊 MOQ Based', desc: 'Flat fee up to MOQ, then per-unit above MOQ' },
                                    ].map(opt => (
                                        <label key={opt.value} style={{
                                            display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 16px',
                                            border: `2px solid ${formData.shippingPolicy?.type === opt.value ? '#4CAF50' : '#e0e0e0'}`,
                                            borderRadius: '10px', cursor: 'pointer', flex: '1', minWidth: '180px',
                                            background: formData.shippingPolicy?.type === opt.value ? '#f0fff0' : '#fff',
                                            transition: 'all 0.2s',
                                        }}>
                                            <input type="radio" name="shippingPolicyType" value={opt.value}
                                                checked={formData.shippingPolicy?.type === opt.value}
                                                onChange={() => setFormData(prev => ({
                                                    ...prev,
                                                    shippingPolicy: { ...prev.shippingPolicy, type: opt.value }
                                                }))}
                                                style={{ marginTop: '2px', accentColor: '#4CAF50' }}
                                            />
                                            <div>
                                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>{opt.label}</div>
                                                <div style={{ fontSize: '12px', color: '#777', marginTop: '2px' }}>{opt.desc}</div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Fee inputs — per-region for international, single for local */}
                            {isInternational ? (
                                <div className="cpShippingTiers" style={{ marginTop: '16px' }}>
                                    {[
                                        { key: 'nearby', label: 'Within Region / Nearby', icon: '📍' },
                                        { key: 'withinCountry', label: 'Within Country', icon: '🏠' },
                                        { key: 'worldwide', label: 'Worldwide', icon: '🌍' },
                                    ].map(tier => {
                                        const regionData = formData.shippingPolicy?.regions?.[tier.key] || {};
                                        const policyType = formData.shippingPolicy?.type || 'byOrder';
                                        const updateRegion = (field, value) => setFormData(prev => ({
                                            ...prev,
                                            shippingPolicy: {
                                                ...prev.shippingPolicy,
                                                regions: {
                                                    ...prev.shippingPolicy?.regions,
                                                    [tier.key]: { ...prev.shippingPolicy?.regions?.[tier.key], [field]: value }
                                                }
                                            }
                                        }));
                                        return (
                                            <div key={tier.key} className="cpShippingTier">
                                                <div className="cpShippingTierHeader">
                                                    <span>{tier.icon} {tier.label}</span>
                                                    <label className="cpCheckboxLabel cpFreeLabel" style={{ marginLeft: 'auto' }}>
                                                        <input type="checkbox" className="cpCheckbox"
                                                            checked={regionData.free || false}
                                                            onChange={(e) => updateRegion('free', e.target.checked)}
                                                        />
                                                        Free Shipping
                                                    </label>
                                                </div>
                                                <div className="cpShippingTierBody">
                                                    {!regionData.free && (
                                                        <>
                                                            {policyType === 'byOrder' && (
                                                                <div className="cpFeeRow">
                                                                    <input type="number" min="0" step="0.01"
                                                                        value={regionData.flatFee ?? ''}
                                                                        onChange={(e) => updateRegion('flatFee', e.target.value)}
                                                                        className={`cpInput ${errors[`shipping_${tier.key}`] ? 'cpInputError' : ''}`}
                                                                        placeholder="Flat fee (USD)"
                                                                    />
                                                                    {currency !== 'USD' && parseFloat(regionData.flatFee) > 0 && (
                                                                        <span className="cpLocalPreview">{formatUsdToLocal(regionData.flatFee, currency, exchangeRate)}</span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {policyType === 'byUnit' && (
                                                                <div className="cpFeeRow">
                                                                    <input type="number" min="0" step="0.01"
                                                                        value={regionData.perUnitFee ?? ''}
                                                                        onChange={(e) => updateRegion('perUnitFee', e.target.value)}
                                                                        className={`cpInput ${errors[`shipping_${tier.key}`] ? 'cpInputError' : ''}`}
                                                                        placeholder="Fee per unit (USD)"
                                                                    />
                                                                    {currency !== 'USD' && parseFloat(regionData.perUnitFee) > 0 && (
                                                                        <span className="cpLocalPreview">{formatUsdToLocal(regionData.perUnitFee, currency, exchangeRate)}</span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {policyType === 'byMOQ' && (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                                    <div className="cpFormGroup" style={{ flex: '1', minWidth: '120px' }}>
                                                                        <label className="cpLabel cpSmallLabel">MOQ Qty</label>
                                                                        <input type="number" min="1" step="1"
                                                                            value={regionData.moqQty ?? ''}
                                                                            onChange={(e) => updateRegion('moqQty', e.target.value)}
                                                                            className={`cpInput ${errors[`shipping_${tier.key}`] ? 'cpInputError' : ''}`}
                                                                            placeholder="e.g. 50"
                                                                        />
                                                                    </div>
                                                                    <div className="cpFormGroup" style={{ flex: '1', minWidth: '120px' }}>
                                                                        <label className="cpLabel cpSmallLabel">Flat fee up to MOQ ($)</label>
                                                                        <input type="number" min="0" step="0.01"
                                                                            value={regionData.moqFlatFee ?? ''}
                                                                            onChange={(e) => updateRegion('moqFlatFee', e.target.value)}
                                                                            className="cpInput" placeholder="e.g. 20"
                                                                        />
                                                                    </div>
                                                                    <div className="cpFormGroup" style={{ flex: '1', minWidth: '120px' }}>
                                                                        <label className="cpLabel cpSmallLabel">Per unit above MOQ ($)</label>
                                                                        <input type="number" min="0" step="0.01"
                                                                            value={regionData.moqPerUnitAbove ?? ''}
                                                                            onChange={(e) => updateRegion('moqPerUnitAbove', e.target.value)}
                                                                            className="cpInput" placeholder="e.g. 0.50"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    <div className="cpFormGroup cpCompact">
                                                        <label className="cpLabel cpSmallLabel">Est. delivery days</label>
                                                        <input type="text"
                                                            value={formData.estimatedDeliveryDays?.[tier.key] || ''}
                                                            onChange={(e) => setFormData(prev => ({
                                                                ...prev,
                                                                estimatedDeliveryDays: { ...prev.estimatedDeliveryDays, [tier.key]: e.target.value }
                                                            }))}
                                                            className="cpInput" placeholder="e.g. 2-4 days"
                                                        />
                                                    </div>
                                                    {errors[`shipping_${tier.key}`] && <span className="cpError">{errors[`shipping_${tier.key}`]}</span>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div style={{ marginTop: '16px' }}>
                                    <label className="cpCheckboxLabel cpFreeLabel" style={{ marginBottom: '12px', display: 'inline-flex' }}>
                                        <input type="checkbox" className="cpCheckbox"
                                            checked={formData.shippingPolicy?.free || false}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                shippingPolicy: { ...prev.shippingPolicy, free: e.target.checked }
                                            }))}
                                        />
                                        Free Shipping
                                    </label>

                                    {!formData.shippingPolicy?.free && (
                                        <>
                                            {formData.shippingPolicy?.type === 'byOrder' && (
                                                <div className="cpFormGroup">
                                                    <label className="cpLabel">Flat Shipping Fee (USD)</label>
                                                    <input type="number" min="0" step="0.01"
                                                        value={formData.shippingPolicy?.flatFee ?? ''}
                                                        onChange={e => setFormData(prev => ({
                                                            ...prev,
                                                            shippingPolicy: { ...prev.shippingPolicy, flatFee: e.target.value }
                                                        }))}
                                                        className={`cpInput ${errors.shippingPolicy ? 'cpInputError' : ''}`}
                                                        placeholder="e.g. 5.00" style={{ maxWidth: '240px' }}
                                                    />
                                                    <span className="cpHelpText">This fee will be charged once per order regardless of quantity.</span>
                                                </div>
                                            )}
                                            {formData.shippingPolicy?.type === 'byUnit' && (
                                                <div className="cpFormGroup">
                                                    <label className="cpLabel">Shipping Fee per Unit (USD)</label>
                                                    <input type="number" min="0" step="0.01"
                                                        value={formData.shippingPolicy?.perUnitFee ?? ''}
                                                        onChange={e => setFormData(prev => ({
                                                            ...prev,
                                                            shippingPolicy: { ...prev.shippingPolicy, perUnitFee: e.target.value }
                                                        }))}
                                                        className={`cpInput ${errors.shippingPolicy ? 'cpInputError' : ''}`}
                                                        placeholder="e.g. 1.50" style={{ maxWidth: '240px' }}
                                                    />
                                                    <span className="cpHelpText">Total shipping = fee × quantity ordered.</span>
                                                </div>
                                            )}
                                            {formData.shippingPolicy?.type === 'byMOQ' && (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                                    <div className="cpFormGroup" style={{ flex: '1', minWidth: '160px' }}>
                                                        <label className="cpLabel">MOQ Quantity</label>
                                                        <input type="number" min="1" step="1"
                                                            value={formData.shippingPolicy?.moqQty ?? ''}
                                                            onChange={e => setFormData(prev => ({
                                                                ...prev,
                                                                shippingPolicy: { ...prev.shippingPolicy, moqQty: e.target.value }
                                                            }))}
                                                            className={`cpInput ${errors.shippingPolicy ? 'cpInputError' : ''}`}
                                                            placeholder="e.g. 50"
                                                        />
                                                        <span className="cpHelpText">Flat fee applies up to this quantity</span>
                                                    </div>
                                                    <div className="cpFormGroup" style={{ flex: '1', minWidth: '160px' }}>
                                                        <label className="cpLabel">Flat Fee up to MOQ (USD)</label>
                                                        <input type="number" min="0" step="0.01"
                                                            value={formData.shippingPolicy?.moqFlatFee ?? ''}
                                                            onChange={e => setFormData(prev => ({
                                                                ...prev,
                                                                shippingPolicy: { ...prev.shippingPolicy, moqFlatFee: e.target.value }
                                                            }))}
                                                            className="cpInput" placeholder="e.g. 20.00"
                                                        />
                                                    </div>
                                                    <div className="cpFormGroup" style={{ flex: '1', minWidth: '160px' }}>
                                                        <label className="cpLabel">Per Unit Above MOQ (USD)</label>
                                                        <input type="number" min="0" step="0.01"
                                                            value={formData.shippingPolicy?.moqPerUnitAbove ?? ''}
                                                            onChange={e => setFormData(prev => ({
                                                                ...prev,
                                                                shippingPolicy: { ...prev.shippingPolicy, moqPerUnitAbove: e.target.value }
                                                            }))}
                                                            className="cpInput" placeholder="e.g. 0.50"
                                                        />
                                                        <span className="cpHelpText">Charged per unit for quantity above MOQ</span>
                                                    </div>
                                                    <div style={{ width: '100%', background: '#f0f9f0', border: '1px solid #c8e6c9', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#2e7d32' }}>
                                                        💡 Example: For {formData.shippingPolicy?.moqQty || 'N'} units → ${formData.shippingPolicy?.moqFlatFee || '0'} flat. For {Number(formData.shippingPolicy?.moqQty || 0) + 10} units → ${formData.shippingPolicy?.moqFlatFee || '0'} + (10 × ${formData.shippingPolicy?.moqPerUnitAbove || '0'}).
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {errors.shippingPolicy && <span className="cpError">{errors.shippingPolicy}</span>}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Cancellation Policy */}
            {!isService && (
                <div className="cpCard">
                    <div className="cpCardHeader">
                        <span className="cpCardIcon">📜</span>
                        <div>
                            <h2 className="cpCardTitle">Cancellation Policy</h2>
                            <p className="cpCardSubtitle">Set your order cancellation rules</p>
                        </div>
                    </div>
                    <div className="cpCardBody">
                        <label className="cpLabel">Can order be cancelled?</label>
                        <div className="cpToggleGroup">
                            <button
                                className={`cpToggleBtn ${formData.cancellable === 'yes' ? 'active' : ''}`}
                                onClick={() => handleInputChange({ target: { name: 'cancellable', value: 'yes' } })}
                            >
                                Yes
                            </button>
                            <button
                                className={`cpToggleBtn ${formData.cancellable === 'no' ? 'active' : ''}`}
                                onClick={() => handleInputChange({ target: { name: 'cancellable', value: 'no' } })}
                            >
                                No
                            </button>
                        </div>
                        {formData.cancellable === 'no' && (
                            <div className="cpInfoBanner cpWarningBanner">
                                <FaInfoCircle />
                                <p>If set to 'No', no refund will be given to the buyer after placing the order.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

// ─── STEP 3: Pricing & Services ──────────────────────────────────────
const PricingServicesStep = ({
    basePrice, setBasePrice, variables, setVariables, presetVariables,
    formData, setFormData, handleInputChange,
    activeTab, setActiveTab, errors, deliveryType,
    packages, setPackages, enabledPackages, setEnabledPackages,
    isEditing, feesObj, currency, exchangeRate,
    addedServices, setAddedServices,
    saleDiscount, setSaleDiscount,
    bulkPricing, setBulkPricing,
    isService,
    buyerQuestions, setBuyerQuestions,
}) => {
    const [showVarForm, setShowVarForm] = useState(false);
    const [editingVarIdx, setEditingVarIdx] = useState(null);
    const [newVarName, setNewVarName] = useState('');
    const [newVarOptions, setNewVarOptions] = useState([{ name: '', price: '', image: '', included: false }]);
    // Services carry their own commission rate, so the earnings preview cannot
    // assume one site-wide percentage.
    const feePercent = sellerFeePercentFor(feesObj?.sellerMatrix, {
        listingType: isService ? 'service' : 'product',
    });
    const briefCharCount = formData.briefDescription.length;
    const [isOfferingModalOpen, setIsOfferingModalOpen] = useState(false);
    const [currentPackageIndex, setCurrentPackageIndex] = useState(null);
    const [newOfferings, setNewOfferings] = useState(['']);

    const parseTime = (timeString) => {
        if (!timeString) return { number: '', unit: '' };
        const parts = timeString.split(' ');
        if (parts.length === 2) {
            const unit = parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase();
            return { number: parts[0], unit };
        }
        return { number: '', unit: '' };
    };

    const addOfferingInput = () => { if (newOfferings.length < 6) setNewOfferings([...newOfferings, '']); };
    const updateOffering = (index, value) => { const u = [...newOfferings]; u[index] = value; setNewOfferings(u); };
    const removeOfferingInput = (index) => setNewOfferings(newOfferings.filter((_, i) => i !== index));

    const openOfferingModal = (index) => {
        setCurrentPackageIndex(index);
        setIsOfferingModalOpen(true);
        setNewOfferings(['']);
    };
    const closeOfferingModal = () => { setIsOfferingModalOpen(false); setNewOfferings(['']); };

    const saveOfferings = () => {
        if (currentPackageIndex !== null) {
            const validOfferings = newOfferings.filter(o => o.trim() !== '');
            const updatedPackages = [...packages];
            const currentOfferings = updatedPackages[currentPackageIndex].options.offerings || [];
            const remaining = 6 - currentOfferings.length;
            const toAdd = validOfferings.slice(0, remaining);
            if (toAdd.length < validOfferings.length) showToast("Maximum of 6 offerings allowed per package", "error");
            updatedPackages[currentPackageIndex].options.offerings = [
                ...currentOfferings,
                ...toAdd.map(description => ({ description }))
            ];
            setPackages(updatedPackages);
            closeOfferingModal();
        }
    };

    const removeOffering = (packageIndex, offeringIndex) => {
        const updated = [...packages];
        updated[packageIndex].options.offerings.splice(offeringIndex, 1);
        setPackages(updated);
    };

    const handleRemoveVariable = (index) => setVariables(variables.filter((_, i) => i !== index));
    const handleEditVariable = (index) => {
        const variable = variables[index];
        setEditingVarIdx(index);
        setNewVarName(variable.name);
        setNewVarOptions(variable.options.map(opt => ({
            name: opt.name,
            price: opt.price === 0 ? '' : String(opt.price),
            image: opt.image || '',
            included: opt.price === 0,
        })));
        setShowVarForm(true);
    };

    const saveVariable = () => {
        const name = newVarName.trim();
        const options = newVarOptions
            .filter(opt => opt.name?.trim())
            .map(opt => ({
                name: opt.name.trim(),
                price: opt.included ? 0 : (parseFloat(opt.price) || 0),
                image: opt.image || null,
            }));
        if (!name || options.length === 0) return;
        if (editingVarIdx !== null) {
            setVariables(variables.map((v, i) => i === editingVarIdx ? { name, options } : v));
        } else {
            setVariables([...variables, { name, options }]);
        }
        setShowVarForm(false);
        setEditingVarIdx(null);
        setNewVarName('');
        setNewVarOptions([{ name: '', price: '', image: '', included: false }]);
    };

    const handlePackageFieldChange = (index, field, value) => {
        const updated = [...packages];
        if (field === 'subtitle' || field === 'briefDescription') {
            updated[index][field] = value;
        } else {
            updated[index].options[field] = field === 'basePrice' ? parseFloat(value) : value;
        }
        setPackages(updated);
    };

    const togglePackage = (pkgName) => {
        setEnabledPackages(prev => {
            if (prev.includes(pkgName)) {
                if (prev.length <= 1) {
                    showToast("At least one package must be enabled", "error");
                    return prev;
                }
                return prev.filter(p => p !== pkgName);
            }
            return [...prev, pkgName];
        });
    };

    const activePackages = packages.filter(pkg => enabledPackages.includes(pkg.name));

    const renderTimeInput = (label, timeValue, onChange, errorKey) => (
        <div className="cpTimeRow">
            <input
                type="number"
                className={`cpInput cpTimeNumber ${errors[errorKey] ? 'cpInputError' : ''}`}
                value={parseTime(timeValue).number}
                onChange={(e) => {
                    const num = e.target.value;
                    const unit = parseTime(timeValue).unit || 'Days';
                    onChange(num ? `${num} ${unit}` : '');
                }}
                placeholder="#"
                min="1"
            />
            <select
                className="cpInput cpTimeUnit"
                value={parseTime(timeValue).unit || 'Days'}
                onChange={(e) => {
                    const unit = e.target.value;
                    const num = parseTime(timeValue).number;
                    onChange(num ? `${num} ${unit}` : '');
                }}
            >
                <option value="Minutes">Minutes</option>
                <option value="Hours">Hours</option>
                <option value="Days">Days</option>
            </select>
        </div>
    );

    const renderPackagesSection = () => (
        <div className="cpPackagesSection">
            <div className="cpPackageToggles">
                <span className="cpLabel">Enable Packages:</span>
                <div className="cpPackageToggleGroup">
                    {['Basic', 'Standard', 'Premium'].map(name => (
                        <label key={name} className={`cpPackageToggle ${enabledPackages.includes(name) ? 'active' : ''}`}>
                            <input
                                type="checkbox"
                                checked={enabledPackages.includes(name)}
                                onChange={() => togglePackage(name)}
                            />
                            {name}
                        </label>
                    ))}
                </div>
                <span className="cpHelpText">Select which packages to offer. You can use 1, 2, or all 3.</span>
            </div>

            <div className="cpPackagesTable">
                <div className="cpPackagesRow cpPackagesHeaderRow">
                    <div className="cpPackagesLabel"></div>
                    {packages.map((pkg, index) => {
                        const isEnabled = enabledPackages.includes(pkg.name);
                        return (
                            <div key={index} className={`cpPackageCol ${!isEnabled ? 'cpPackageDisabled' : ''}`}>
                                <span className={`cpPackageBadge cpPackageBadge${pkg.name}`}>{pkg.name}</span>
                                {!isEnabled && <span className="cpPackageDisabledLabel">Disabled</span>}
                            </div>
                        );
                    })}
                </div>

                <div className="cpPackagesRow">
                    <div className="cpPackagesLabel">Package Name</div>
                    {packages.map((pkg) => {
                        const isEnabled = enabledPackages.includes(pkg.name);
                        const idx = packages.indexOf(pkg);
                        return (
                            <div key={pkg.name} className={`cpPackageCol ${!isEnabled ? 'cpPackageDisabled' : ''}`}>
                                <input
                                    type="text"
                                    value={pkg.subtitle}
                                    onChange={(e) => handlePackageFieldChange(idx, 'subtitle', e.target.value)}
                                    className={`cpInput ${errors[`package_${idx}_subtitle`] ? 'cpInputError' : ''}`}
                                    placeholder="Enter package name"
                                    disabled={!isEnabled}
                                />
                                {isEnabled && errors[`package_${idx}_subtitle`] && <span className="cpError">{errors[`package_${idx}_subtitle`]}</span>}
                            </div>
                        );
                    })}
                </div>

                <div className="cpPackagesRow">
                    <div className="cpPackagesLabel">Brief Description</div>
                    {packages.map((pkg) => {
                        const isEnabled = enabledPackages.includes(pkg.name);
                        const idx = packages.indexOf(pkg);
                        return (
                            <div key={pkg.name} className={`cpPackageCol ${!isEnabled ? 'cpPackageDisabled' : ''}`}>
                                <textarea
                                    value={pkg.briefDescription || ''}
                                    onChange={(e) => handlePackageFieldChange(idx, 'briefDescription', e.target.value)}
                                    className={`cpInput cpSmallTextarea ${errors[`package_${idx}_briefDescription`] ? 'cpInputError' : ''}`}
                                    placeholder="Brief description (max 100 chars)"
                                    maxLength={100}
                                    disabled={!isEnabled}
                                />
                                {isEnabled && <div className="cpCharCount">{(pkg.briefDescription || '').length} / 100</div>}
                                {isEnabled && errors[`package_${idx}_briefDescription`] && <span className="cpError">{errors[`package_${idx}_briefDescription`]}</span>}
                            </div>
                        );
                    })}
                </div>

                <div className="cpPackagesRow">
                    <div className="cpPackagesLabel">Package Offerings / Includes</div>
                    {packages.map((pkg) => {
                        const isEnabled = enabledPackages.includes(pkg.name);
                        const idx = packages.indexOf(pkg);
                        return (
                            <div key={pkg.name} className={`cpPackageCol ${!isEnabled ? 'cpPackageDisabled' : ''}`}>
                                {isEnabled ? (
                                    <>
                                        <button
                                            className="cpAddBtn cpCompactBtn"
                                            onClick={() => pkg.options.offerings.length < 6 && openOfferingModal(idx)}
                                            disabled={pkg.options.offerings.length >= 6}
                                        >
                                            <Plus size={14} /> Add
                                        </button>
                                        {pkg.options.offerings.length > 0 && (
                                            <div className="cpOfferingsList">
                                                {pkg.options.offerings.map((offering, oIdx) => (
                                                    <div key={oIdx} className="cpOfferingChip">
                                                        <span>{offering.description}</span>
                                                        <button onClick={() => removeOffering(idx, oIdx)}>×</button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : <span className="cpDisabledPlaceholder">—</span>}
                            </div>
                        );
                    })}
                </div>

                {deliveryType.includes('delivery') && (
                    <div className="cpPackagesRow">
                        <div className="cpPackagesLabel">Time/Days Prior to Shipment</div>
                        {packages.map((pkg) => {
                            const isEnabled = enabledPackages.includes(pkg.name);
                            const idx = packages.indexOf(pkg);
                            return (
                                <div key={pkg.name} className={`cpPackageCol ${!isEnabled ? 'cpPackageDisabled' : ''}`}>
                                    {isEnabled ? (
                                        <>
                                            {renderTimeInput('', pkg.options.timeToDeliver,
                                                (val) => handlePackageFieldChange(idx, 'timeToDeliver', val),
                                                `package_${idx}_timeToDeliver`
                                            )}
                                            {errors[`package_${idx}_timeToDeliver`] && <span className="cpError">{errors[`package_${idx}_timeToDeliver`]}</span>}
                                        </>
                                    ) : <span className="cpDisabledPlaceholder">—</span>}
                                </div>
                            );
                        })}
                    </div>
                )}

                {deliveryType.includes('pickup') && (
                    <div className="cpPackagesRow">
                        <div className="cpPackagesLabel">Timeframe for Order to be Ready</div>
                        {packages.map((pkg) => {
                            const isEnabled = enabledPackages.includes(pkg.name);
                            const idx = packages.indexOf(pkg);
                            return (
                                <div key={pkg.name} className={`cpPackageCol ${!isEnabled ? 'cpPackageDisabled' : ''}`}>
                                    {isEnabled ? (
                                        <>
                                            {renderTimeInput('', pkg.options.timeframe,
                                                (val) => handlePackageFieldChange(idx, 'timeframe', val),
                                                `package_${idx}_timeframe`
                                            )}
                                            {errors[`package_${idx}_timeframe`] && <span className="cpError">{errors[`package_${idx}_timeframe`]}</span>}
                                        </>
                                    ) : <span className="cpDisabledPlaceholder">—</span>}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="cpPackagesRow">
                    <div className="cpPackagesLabel">Price (USD)</div>
                    {packages.map((pkg) => {
                        const isEnabled = enabledPackages.includes(pkg.name);
                        const idx = packages.indexOf(pkg);
                        return (
                            <div key={pkg.name} className={`cpPackageCol ${!isEnabled ? 'cpPackageDisabled' : ''}`}>
                                <input
                                    type="number"
                                    value={pkg.options.basePrice}
                                    onChange={(e) => handlePackageFieldChange(idx, 'basePrice', e.target.value)}
                                    min="1"
                                    className={`cpInput ${errors[`package_${idx}_basePrice`] ? 'cpInputError' : ''}`}
                                    placeholder="Price in USD"
                                    disabled={!isEnabled}
                                />
                                {isEnabled && currency !== 'USD' && pkg.options.basePrice > 0 && (
                                    <span className="cpLocalPreview">{formatUsdToLocal(pkg.options.basePrice, currency, exchangeRate)}</span>
                                )}
                                {isEnabled && errors[`package_${idx}_basePrice`] && <span className="cpError">{errors[`package_${idx}_basePrice`]}</span>}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const renderVariableSection = () => (
        <div className="cpVariableSection">
            <div className="cpFormGroup">
                <label className="cpLabel">Brief Description <span className="cpRequired">*</span></label>
                <textarea
                    className={`cpInput cpSmallTextarea ${errors.briefDescription ? 'cpInputError' : ''}`}
                    name="briefDescription"
                    value={formData.briefDescription}
                    onChange={handleInputChange}
                    placeholder="Brief description of the product with variables"
                    maxLength={100}
                />
                <div className="cpCharCount">{briefCharCount} / 100</div>
                {errors.briefDescription && <span className="cpError">{errors.briefDescription}</span>}
            </div>

            <div className="cpFormGroup">
                <label className="cpLabel">Base Price (USD) <span className="cpRequired">*</span></label>
                <input
                    type="number"
                    className={`cpInput ${errors.basePrice ? 'cpInputError' : ''}`}
                    placeholder="Set base price"
                    value={basePrice}
                    onChange={(e) => setBasePrice(e.target.value)}
                    min="1"
                />
                {currency !== 'USD' && parseFloat(basePrice) > 0 && (
                    <span className="cpLocalPreview">{formatUsdToLocal(basePrice, currency, exchangeRate)}</span>
                )}
                {errors.basePrice && <span className="cpError">{errors.basePrice}</span>}
            </div>

            {variables.length > 0 && (
                <div className="cpVariablesList">
                    {variables.map((variable, index) => (
                        <div key={index} className="cpVariableCard">
                            <div className="cpVariableCardHeader">
                                <h4>{variable.name}</h4>
                                <div className="cpVariableCardActions">
                                    <button className="cpEditBtn" onClick={() => handleEditVariable(index)}>
                                        <Pencil size={15} />
                                    </button>
                                    <button className="cpRemoveBtn" onClick={() => handleRemoveVariable(index)}>
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="cpOptionsGrid">
                                {variable.options.map((option, optIndex) => (
                                    <div key={optIndex} className="cpOptionChip">
                                        {option.image && <img src={option.image} alt="" className="cpOptionImg" />}
                                        <span className="cpOptionName">{option.name}</span>
                                        {option.price === 0
                                            ? <span className="cpOptionIncluded">Included</span>
                                            : <span className="cpOptionPrice">+{formatCurrencyDual(option.price, currency, exchangeRate)}</span>
                                        }
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showVarForm ? (
                <div className="cpVariableFormInline">
                    <div className="cpFormGroup">
                        <label className="cpLabel">Variable Name</label>
                        <input type="text" className="cpInput" value={newVarName}
                            onChange={(e) => setNewVarName(e.target.value)} placeholder="e.g. Color, Size" />
                    </div>
                    <div className="cpFormGroup">
                        <label className="cpLabel">Options</label>
                        {newVarOptions.map((opt, oi) => (
                            <div key={oi} className="cpDetailRow cpVarOptRow" style={{ marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                                <input type="text" className="cpInput" placeholder="Option name (e.g. Red, Large)"
                                    value={opt.name} onChange={(e) => {
                                        const u = [...newVarOptions]; u[oi] = { ...u[oi], name: e.target.value }; setNewVarOptions(u);
                                    }} />
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontSize: '13px', cursor: 'pointer', margin: '0 4px' }}>
                                    <input type="checkbox" checked={opt.included || false} onChange={(e) => {
                                        const u = [...newVarOptions]; u[oi] = { ...u[oi], included: e.target.checked, price: e.target.checked ? '' : u[oi].price }; setNewVarOptions(u);
                                    }} />
                                    Included
                                </label>
                                {!opt.included && (
                                    <input type="number" className="cpInput" placeholder="Extra price (USD)"
                                        value={opt.price} onChange={(e) => {
                                            const u = [...newVarOptions]; u[oi] = { ...u[oi], price: e.target.value }; setNewVarOptions(u);
                                        }} step="0.01" style={{ maxWidth: 140 }} />
                                )}
                                {currency !== 'USD' && !opt.included && parseFloat(opt.price) > 0 && (
                                    <span className="cpLocalPreview">{formatUsdToLocal(opt.price, currency, exchangeRate)}</span>
                                )}
                                <div className="optionImageUpload">
                                    <input type="file" accept="image/*" id={`cpvaropt-img-${oi}`} style={{ display: 'none' }}
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    const u = [...newVarOptions]; u[oi] = { ...u[oi], image: reader.result }; setNewVarOptions(u);
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }} />
                                    <label htmlFor={`cpvaropt-img-${oi}`} className="imageUploadLabel" style={{ cursor: 'pointer' }}>
                                        {opt.image ? (
                                            <img src={opt.image} alt="Option preview" style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover' }} />
                                        ) : (
                                            <div className="uploadPlaceholder" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '4px 8px', border: '1px dashed #d1d5db', borderRadius: 6, color: '#9ca3af' }}><Plus size={12} /> Img</div>
                                        )}
                                    </label>
                                </div>
                                {newVarOptions.length > 1 && (
                                    <button className="cpRemoveBtn" onClick={() => setNewVarOptions(newVarOptions.filter((_, idx) => idx !== oi))}>
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                        ))}
                        {newVarOptions.length < 6 && (
                            <button className="cpAddBtn cpCompactBtn" onClick={() => setNewVarOptions([...newVarOptions, { name: '', price: '', image: '', included: false }])}>
                                <Plus size={14} /> Add Option
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button className="cpNavBtn cpNavBtnNext" style={{ padding: '8px 16px', fontSize: 14 }} onClick={saveVariable}>
                            {editingVarIdx !== null ? 'Update Variable' : 'Save Variable'}
                        </button>
                        <button className="cpNavBtn cpNavBtnBack" style={{ padding: '8px 16px', fontSize: 14 }} onClick={() => {
                            setShowVarForm(false); setEditingVarIdx(null); setNewVarName(''); setNewVarOptions([{ name: '', price: '', image: '', included: false }]);
                        }}>Cancel</button>
                    </div>
                </div>
            ) : (
                <button className="cpAddBtn" onClick={() => setShowVarForm(true)}>
                    <Plus size={16} /> Add Variable
                </button>
            )}

            {deliveryType.includes('delivery') && (
                <div className="cpFormGroup">
                    <label className="cpLabel">Time/Days Prior to Shipment</label>
                    {renderTimeInput('', formData.timeToDeliver,
                        (val) => setFormData(prev => ({ ...prev, timeToDeliver: val })),
                        'timeToDeliver'
                    )}
                    {errors.timeToDeliver && <span className="cpError">{errors.timeToDeliver}</span>}
                </div>
            )}
            {deliveryType.includes('pickup') && (
                <div className="cpFormGroup">
                    <label className="cpLabel">Timeframe for Order to be Ready</label>
                    {renderTimeInput('', formData.timeframe,
                        (val) => setFormData(prev => ({ ...prev, timeframe: val })),
                        'timeframe'
                    )}
                    {errors.timeframe && <span className="cpError">{errors.timeframe}</span>}
                </div>
            )}
        </div>
    );

    return (
        <>
            {/* Pricing */}
            <div className="cpCard">
                <div className="cpCardHeader">
                    <span className="cpCardIcon">💰</span>
                    <div>
                        <h2 className="cpCardTitle">{activeTab === 'withoutVariable' ? 'Packages & Pricing' : 'Pricing & Variables'}</h2>
                        <p className="cpCardSubtitle">Set your pricing model</p>
                    </div>
                </div>
                <div className="cpCardBody">
                    {!isEditing && (
                        <div className="cpPricingTabs">
                            <button
                                className={`cpPricingTab ${activeTab === 'withoutVariable' ? 'active' : ''}`}
                                onClick={() => setActiveTab('withoutVariable')}
                            >
                                <Package size={16} /> Packages
                            </button>
                            <button
                                className={`cpPricingTab ${activeTab === 'variable' ? 'active' : ''}`}
                                onClick={() => setActiveTab('variable')}
                            >
                                <Tag size={16} /> Regular (Variables)
                            </button>
                        </div>
                    )}

                    {!isEditing && (
                        <div className="cpInfoBanner">
                            <FaInfoCircle />
                            <p>
                                {activeTab === 'variable'
                                    ? 'Offer product variations like size, add-ons, or toppings so buyers can customize their purchase.'
                                    : 'Create up to three packages (Basic, Standard, Premium) with unique combinations for different customer preferences.'}
                            </p>
                        </div>
                    )}

                    {activeTab === 'variable' ? renderVariableSection() : renderPackagesSection()}

                    {feesObj?.seller != null && currency != null && exchangeRate != null && (
                        <div className="cpEarningsBanner">
                            <strong>You will receive per sale (after {feePercent}% service fee):</strong>
                            {activeTab === 'variable' ? (
                                (() => {
                                    const price = parseFloat(basePrice) || 0;
                                    const delFee = deliveryType.includes('delivery') && !formData.shippingPolicy?.free
                                        ? (formData.shippingPolicy?.type === 'byOrder' ? parseFloat(formData.shippingPolicy?.flatFee) || 0 : 0) : 0;
                                    const gross = price + delFee;
                                    const amt = gross > 0 ? calculateSellerAmount(gross, feePercent) : 0;
                                    return <span> {formatCurrency(amt, currency, exchangeRate)} (base + delivery)</span>;
                                })()
                            ) : (
                                <span>
                                    {activePackages.filter(p => (p.options?.basePrice || 0) > 0).map((pkg, i) => {
                                        const price = pkg.options?.basePrice || 0;
                                        const delFee = deliveryType.includes('delivery') && !formData.shippingPolicy?.free
                                            ? (formData.shippingPolicy?.type === 'byOrder' ? parseFloat(formData.shippingPolicy?.flatFee) || 0 : 0) : 0;
                                        const gross = price + delFee;
                                        const amt = gross > 0 ? calculateSellerAmount(gross, feePercent) : 0;
                                        return <span key={i}>{i > 0 ? ', ' : ' '}{pkg.name}: {formatCurrencyDual(amt, currency, exchangeRate)}</span>;
                                    })}
                                    {activePackages.every(p => !(p.options?.basePrice || 0)) && ' Set prices above to see earnings'}
                                </span>
                            )}
                            <PayoutFeeNote />
                        </div>
                    )}
                </div>
            </div>

            {/* Sale Discount */}
            <div className="cpCard">
                <div className="cpCardHeader">
                    <span className="cpCardIcon">🏷️</span>
                    <div>
                        <h2 className="cpCardTitle">Discount & Promotions</h2>
                        <p className="cpCardSubtitle">Attract more buyers with discounts and bulk pricing</p>
                    </div>
                </div>
                <div className="cpCardBody">
                    <h3 className="cpSectionLabel">Sale Discount</h3>
                    <p className="cpHelpText">Set a percentage or fixed amount discount with an end date.</p>

                    <div className="cpFormGrid cpFormTriple">
                        <div className="cpFormGroup">
                            <label className="cpLabel">Discount Type</label>
                            <div className="cpToggleGroup">
                                <button
                                    className={`cpToggleBtn ${saleDiscount.type === 'percentage' ? 'active' : ''}`}
                                    onClick={() => setSaleDiscount(prev => ({ ...prev, type: 'percentage' }))}
                                >
                                    <FaPercent /> Percentage
                                </button>
                                <button
                                    className={`cpToggleBtn ${saleDiscount.type === 'fixed' ? 'active' : ''}`}
                                    onClick={() => setSaleDiscount(prev => ({ ...prev, type: 'fixed' }))}
                                >
                                    <DollarSign size={14} /> Fixed (USD)
                                </button>
                            </div>
                        </div>
                        <div className="cpFormGroup">
                            <label className="cpLabel">{saleDiscount.type === 'percentage' ? 'Discount %' : 'Discount Amount (USD)'}</label>
                            <input
                                type="number"
                                className="cpInput"
                                placeholder={saleDiscount.type === 'percentage' ? 'e.g. 15' : 'e.g. 5.00'}
                                value={saleDiscount.value}
                                onChange={(e) => setSaleDiscount(prev => ({ ...prev, value: e.target.value }))}
                                min="0"
                                max={saleDiscount.type === 'percentage' ? '100' : undefined}
                            />
                            {saleDiscount.type === 'fixed' && currency !== 'USD' && parseFloat(saleDiscount.value) > 0 && (
                                <span className="cpLocalPreview">{formatUsdToLocal(saleDiscount.value, currency, exchangeRate)}</span>
                            )}
                        </div>
                        <div className="cpFormGroup">
                            <label className="cpLabel">Sale Ends On</label>
                            <input
                                type="date"
                                className="cpInput"
                                value={saleDiscount.endDate}
                                onChange={(e) => setSaleDiscount(prev => ({ ...prev, endDate: e.target.value }))}
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                    </div>

                    {saleDiscount.value > 0 && (
                        <div className="cpDiscountPreview">
                            <FaTags />
                            <span>
                                {saleDiscount.type === 'percentage'
                                    ? <>{saleDiscount.value}% off — </>
                                    : <>{formatCurrencyDual(saleDiscount.value, currency, exchangeRate)} off — </>}
                                {(() => {
                                    const price = activeTab === 'variable'
                                        ? parseFloat(basePrice) || 0
                                        : activePackages[0]?.options?.basePrice || 0;
                                    if (price <= 0) return 'Set a price to preview';
                                    const discounted = saleDiscount.type === 'percentage'
                                        ? price * (1 - saleDiscount.value / 100)
                                        : price - saleDiscount.value;
                                    return (
                                        <>
                                            {formatCurrencyDual(price, currency, exchangeRate)} → {formatCurrencyDual(Math.max(0, discounted), currency, exchangeRate)}
                                        </>
                                    );
                                })()}
                                {saleDiscount.endDate && ` (ends ${new Date(saleDiscount.endDate).toLocaleDateString()})`}
                            </span>
                        </div>
                    )}

                    <div className="cpDivider" />

                    {!isService && (
                        <>
                            <h3 className="cpSectionLabel">Bulk Pricing</h3>
                            <p className="cpHelpText">Offer different per-unit prices based on quantity purchased. For package pricing, you can set separate bulk prices per package.</p>

                            {bulkPricing.map((tier, index) => (
                                <div key={index} className="cpBulkRow">
                                    {activeTab === 'withoutVariable' && (
                                        <div className="cpFormGroup">
                                            <label className="cpLabel">Package</label>
                                            <Dropdown
                                                options={activePackages.map(p => ({ value: p.name, label: p.name }))}
                                                value={tier.packageName ? { value: tier.packageName, label: tier.packageName } : null}
                                                onChange={(selected) => {
                                                    const updated = [...bulkPricing];
                                                    updated[index].packageName = selected?.value || '';
                                                    setBulkPricing(updated);
                                                }}
                                                placeholder="All packages (optional)"
                                                isClearable
                                            />
                                        </div>
                                    )}
                                    <div className="cpFormGroup">
                                        <label className="cpLabel">Min. Quantity</label>
                                        <input
                                            type="number"
                                            className="cpInput"
                                            placeholder="e.g. 3"
                                            value={tier.minQuantity}
                                            onChange={(e) => {
                                                const updated = [...bulkPricing];
                                                updated[index].minQuantity = e.target.value;
                                                setBulkPricing(updated);
                                            }}
                                            min="1"
                                        />
                                    </div>
                                    <div className="cpFormGroup">
                                        <label className="cpLabel">Price per Unit (USD)</label>
                                        <input
                                            type="number"
                                            className="cpInput"
                                            placeholder="e.g. 4.80"
                                            value={tier.price}
                                            onChange={(e) => {
                                                const updated = [...bulkPricing];
                                                updated[index].price = e.target.value;
                                                setBulkPricing(updated);
                                            }}
                                            min="0"
                                            step="0.01"
                                        />
                                        {currency !== 'USD' && parseFloat(tier.price) > 0 && (
                                            <span className="cpLocalPreview">{formatUsdToLocal(tier.price, currency, exchangeRate)}</span>
                                        )}
                                    </div>
                                    <button className="cpRemoveBtn" onClick={() => setBulkPricing(bulkPricing.filter((_, i) => i !== index))}>
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                            <button className="cpAddBtn" onClick={() => setBulkPricing([...bulkPricing, { minQuantity: '', price: '', packageName: '' }])}>
                                <Plus size={16} /> Add Tier
                            </button>

                            {bulkPricing.length > 0 && (
                                <div className="cpBulkPreview">
                                    <strong>Preview:</strong>
                                    <div className="cpBulkTags">
                                        {bulkPricing.filter(t => t.minQuantity && t.price).map((tier, i) => (
                                            <span key={i} className="cpBulkTag">
                                                {(tier.packageName ? `${tier.packageName} - ` : '')}{tier.minQuantity}+ pcs: {formatCurrencyDual(parseFloat(tier.price), currency, exchangeRate)}
                                                {saleDiscount.value > 0 && (
                                                    <> → {formatCurrencyDual(Math.max(0, saleDiscount.type === 'percentage'
                                                        ? (parseFloat(tier.price) * (1 - (parseFloat(saleDiscount.value) || 0) / 100))
                                                        : (parseFloat(tier.price) - (parseFloat(saleDiscount.value) || 0))
                                                    ), currency, exchangeRate)} with sale</>
                                                )}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Added Services */}
            <div className="cpCard">
                <div className="cpCardHeader">
                    <span className="cpCardIcon">✨</span>
                    <div>
                        <h2 className="cpCardTitle">Added Services</h2>
                        <p className="cpCardSubtitle">Offer extra services like special packing, cards, stitching, etc.</p>
                    </div>
                </div>
                <div className="cpCardBody">
                    {addedServices.length > 0 && (
                        <div className="cpAddedServicesList">
                            {addedServices.map((svc, index) => (
                                <div key={index} className="cpAddedServiceCard">
                                    <div className="cpAddedServiceMain">
                                        <div className="cpFormGroup">
                                            <label className="cpLabel">Service Name <span className="cpRequired">*</span></label>
                                            <input
                                                type="text"
                                                className="cpInput"
                                                placeholder="e.g. Gift Wrapping"
                                                value={svc.name}
                                                onChange={(e) => {
                                                    const updated = [...addedServices];
                                                    updated[index].name = e.target.value;
                                                    setAddedServices(updated);
                                                }}
                                            />
                                        </div>
                                        <div className="cpFormGroup">
                                            <label className="cpLabel">Description</label>
                                            <input
                                                type="text"
                                                className="cpInput"
                                                placeholder="Brief description"
                                                value={svc.description}
                                                onChange={(e) => {
                                                    const updated = [...addedServices];
                                                    updated[index].description = e.target.value;
                                                    setAddedServices(updated);
                                                }}
                                            />
                                        </div>
                                        <div className="cpFormGroup cpFormSmall">
                                            <label className="cpLabel">Price (USD)</label>
                                            <input
                                                type="number"
                                                className="cpInput"
                                                placeholder="0 = Free"
                                                value={svc.price}
                                                onChange={(e) => {
                                                    const updated = [...addedServices];
                                                    updated[index].price = e.target.value;
                                                    setAddedServices(updated);
                                                }}
                                                min="0"
                                                step="0.01"
                                            />
                                            {currency !== 'USD' && parseFloat(svc.price) > 0 && (
                                                <span className="cpLocalPreview">{formatUsdToLocal(svc.price, currency, exchangeRate)}</span>
                                            )}
                                        </div>
                                        <div className="cpFormGroup cpFormSmall">
                                            <label className="cpLabel">Pricing Mode</label>
                                            <select
                                                className="cpSelect"
                                                value={svc.pricingMode || 'perOrder'}
                                                onChange={(e) => {
                                                    const updated = [...addedServices];
                                                    updated[index].pricingMode = e.target.value;
                                                    setAddedServices(updated);
                                                }}
                                            >
                                                <option value="perOrder">Per Order</option>
                                                <option value="perUnit">Per Unit</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="cpAddedServiceActions">
                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                            <label className="cpServiceImageUpload">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => {
                                                                const updated = [...addedServices];
                                                                updated[index].image = reader.result;
                                                                setAddedServices(updated);
                                                            };
                                                            reader.readAsDataURL(file);
                                                        }
                                                    }}
                                                />
                                                {svc.image ? (
                                                    <img src={svc.image} alt="Service" className="cpServiceImagePreview" />
                                                ) : (
                                                    <div className="cpServiceImagePlaceholder">
                                                        <FaImage /> Photo
                                                    </div>
                                                )}
                                            </label>
                                            {svc.image && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const updated = [...addedServices];
                                                        updated[index].image = null;
                                                        setAddedServices(updated);
                                                    }}
                                                    style={{ position: 'absolute', top: -8, right: -8, background: '#ef4444', color: 'white', borderRadius: '50%', border: 'none', width: 22, height: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, fontSize: '14px', zIndex: 2 }}
                                                >
                                                    &times;
                                                </button>
                                            )}
                                        </div>
                                        <button className="cpRemoveBtn" onClick={() => setAddedServices(addedServices.filter((_, i) => i !== index))}>
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <button className="cpAddBtn" onClick={() => setAddedServices([...addedServices, { name: '', description: '', price: '', image: null }])}>
                        <Plus size={16} /> Add Service
                    </button>
                </div>
            </div>

            {/* Buyer Questions */}
            <div className="cpCard">
                <div className="cpCardHeader">
                    <span className="cpCardIcon">❓</span>
                    <div>
                        <h2 className="cpCardTitle">Buyer Requirements</h2>
                        <p className="cpCardSubtitle">Ask buyers questions they must answer before ordering (e.g. "What text on the cake?"). You can attach an image for reference.</p>
                    </div>
                </div>
                <div className="cpCardBody">
                    {buyerQuestions.length > 0 && (
                        <div className="cpAddedServicesList">
                            {buyerQuestions.map((q, index) => (
                                <div key={index} className="cpAddedServiceCard">
                                    <div className="cpAddedServiceMain" style={{ flex: 1 }}>
                                        <div className="cpFormGroup">
                                            <label className="cpLabel">Question <span className="cpRequired">*</span></label>
                                            <input
                                                type="text"
                                                className="cpInput"
                                                placeholder="e.g. What text should be written on the cake?"
                                                value={q.question}
                                                onChange={(e) => {
                                                    const updated = [...buyerQuestions];
                                                    updated[index].question = e.target.value;
                                                    setBuyerQuestions(updated);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="cpAddedServiceActions">
                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                            <label className="cpServiceImageUpload">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => {
                                                                const updated = [...buyerQuestions];
                                                                updated[index].image = reader.result;
                                                                setBuyerQuestions(updated);
                                                            };
                                                            reader.readAsDataURL(file);
                                                        }
                                                    }}
                                                />
                                                {q.image ? (
                                                    <img src={q.image} alt="Reference" className="cpServiceImagePreview" />
                                                ) : (
                                                    <div className="cpServiceImagePlaceholder">
                                                        <FaImage /> Example
                                                    </div>
                                                )}
                                            </label>
                                            {q.image && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const updated = [...buyerQuestions];
                                                        updated[index].image = null;
                                                        setBuyerQuestions(updated);
                                                    }}
                                                    style={{ position: 'absolute', top: -8, right: -8, background: '#ef4444', color: 'white', borderRadius: '50%', border: 'none', width: 22, height: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, fontSize: '14px', zIndex: 2 }}
                                                >
                                                    &times;
                                                </button>
                                            )}
                                        </div>
                                        <button className="cpRemoveBtn" onClick={() => setBuyerQuestions(buyerQuestions.filter((_, i) => i !== index))}>
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <button className="cpAddBtn" onClick={() => setBuyerQuestions([...buyerQuestions, { question: '', image: '' }])}>
                        <Plus size={16} /> Add Question
                    </button>
                </div>
            </div>


            {isOfferingModalOpen && (
                <div className="modalOverlay">
                    <div className="modalContent" style={{ maxWidth: '600px' }}>
                        <div className="modalHeader">
                            <h3>Add Offerings / Includes</h3>
                            <button className="closeButton" onClick={closeOfferingModal}><X size={20} /></button>
                        </div>
                        <div className="modalBody">
                            {newOfferings.map((offering, index) => (
                                <div key={index} className="optionGroup">
                                    <input
                                        type="text"
                                        placeholder="Enter offering description"
                                        value={offering}
                                        onChange={(e) => updateOffering(index, e.target.value)}
                                        className="inputField"
                                    />
                                    {newOfferings.length > 1 && (
                                        <button className="removeOptionButton" onClick={() => removeOfferingInput(index)}>
                                            <Minus size={20} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button className="addOptionButton" onClick={addOfferingInput}>
                                <Plus size={16} /> Add Another
                            </button>
                        </div>
                        <div className="modalFooter">
                            <button className="primaryBtn" onClick={saveOfferings} disabled={newOfferings.every(o => o.trim() === '')}>
                                Save Offerings
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// ─── B2B STEP 1: Listing Details ────────────────────────────────────
const B2BDetailsStep = ({ formData, handleInputChange, errors, setFormData, b2bCategories, featureTable, setFeatureTable }) => {
    const charCount = formData.productDescription.length;
    const selectedmaincategory = b2bCategories.find(cat => cat.name === formData.maincategory);
    const selectedsubcategory = selectedmaincategory?.subcategories?.find(sub => sub.name === formData.subcategory);

    const handleCategoryChange = (selected) => {
        setFormData(prev => ({ ...prev, maincategory: selected.value, subcategory: '', childcategory: '' }));
    };

    return (
        <div className="cpCard">
            <div className="cpCardHeader">
                <span className="cpCardIcon">📋</span>
                <div>
                    <h2 className="cpCardTitle">B2B Listing Details</h2>
                    <p className="cpCardSubtitle">Basic information about your wholesale listing</p>
                </div>
            </div>
            <div className="cpCardBody">
                <div className="cpFormGrid">
                    <div className="cpFormGroup cpFormFull">
                        <label className="cpLabel">Listing Title <span className="cpRequired">*</span></label>
                        <input type="text" className={`cpInput ${errors.productTitle ? 'cpInputError' : ''}`}
                            name="productTitle" value={formData.productTitle} onChange={handleInputChange}
                            placeholder="Enter a title e.g. 'Custom Printed T-Shirts - Bulk Orders'" />
                        {errors.productTitle && <span className="cpError">{errors.productTitle}</span>}
                    </div>
                    <div className="cpFormGroup cpFormFull">
                        <label className="cpLabel">Listing Description <span className="cpRequired">*</span></label>
                        <textarea className={`cpInput cpTextarea ${errors.productDescription ? 'cpInputError' : ''}`}
                            name="productDescription" value={formData.productDescription} onChange={handleInputChange}
                            placeholder="Detailed description for wholesale buyers" maxLength={1200} rows={4} />
                        <div className="cpCharCount">{charCount} / 1200</div>
                        {errors.productDescription && <span className="cpError">{errors.productDescription}</span>}
                    </div>
                </div>

                <div className="cpDivider" />

                <h3 className="cpSectionLabel">Category</h3>
                <div className="cpFormGrid">
                    <div className="cpFormGroup">
                        <label className="cpLabel">Main Category <span className="cpRequired">*</span></label>
                        <Dropdown
                            options={b2bCategories.map(cat => ({ value: cat.name, label: cat.name }))}
                            value={formData.maincategory ? { value: formData.maincategory, label: formData.maincategory } : null}
                            onChange={handleCategoryChange} placeholder="Select B2B Category" required />
                        {errors.maincategory && <span className="cpError">{errors.maincategory}</span>}
                    </div>
                    {selectedmaincategory && (
                        <div className="cpFormGroup">
                            <label className="cpLabel">Sub Category <span className="cpRequired">*</span></label>
                            <Dropdown
                                options={selectedmaincategory.subcategories.map(sub => ({ value: sub.name, label: sub.name }))}
                                value={formData.subcategory ? { value: formData.subcategory, label: formData.subcategory } : null}
                                onChange={(selected) => setFormData(prev => ({ ...prev, subcategory: selected.value, childcategory: '' }))}
                                placeholder="Select Sub Category" required />
                            {errors.subcategory && <span className="cpError">{errors.subcategory}</span>}
                        </div>
                    )}
                    {selectedsubcategory && selectedsubcategory.children?.length > 0 && (
                        <div className="cpFormGroup">
                            <label className="cpLabel">Child Category</label>
                            <Dropdown
                                options={selectedsubcategory.children.map(child => ({ value: child.name, label: child.name }))}
                                value={formData.childcategory ? { value: formData.childcategory, label: formData.childcategory } : null}
                                onChange={(selected) => setFormData(prev => ({ ...prev, childcategory: selected.value }))}
                                placeholder="Select Child Category (Optional)" isClearable />
                        </div>
                    )}
                </div>

                <div className="cpDivider" />

                <h3 className="cpSectionLabel">B2B Configuration</h3>
                <div className="cpFormGrid">
                    <div className="cpFormGroup">
                        <label className="cpLabel">Listing Type</label>
                        <Dropdown
                            options={[{ value: 'product', label: 'Product' }, { value: 'service', label: 'Service' }]}
                            value={{ value: formData.listingType, label: formData.listingType === 'service' ? 'Service' : 'Product' }}
                            onChange={(selected) => setFormData(prev => ({ ...prev, listingType: selected.value }))}
                            placeholder="Select listing type" />
                    </div>
                    <div className="cpFormGroup">
                        <label className="cpLabel">Listing Mode <span className="cpRequired">*</span></label>
                        <Dropdown
                            options={[
                                { value: 'wholesale', label: 'Wholesale Only' },
                                { value: 'customization', label: 'Customization Only' },
                                { value: 'both', label: 'Wholesale + Customization' },
                            ]}
                            value={formData.listingMode ? {
                                value: formData.listingMode,
                                label: formData.listingMode === 'wholesale' ? 'Wholesale Only' :
                                    formData.listingMode === 'customization' ? 'Customization Only' : 'Wholesale + Customization'
                            } : null}
                            onChange={(selected) => setFormData(prev => ({ ...prev, listingMode: selected.value }))}
                            placeholder="Select listing mode" />
                        {errors.listingMode && <span className="cpError">{errors.listingMode}</span>}
                    </div>
                    <div className="cpFormGroup">
                        <label className="cpLabel">Minimum Order Quantity (MOQ) <span className="cpRequired">*</span></label>
                        <input type="number" className={`cpInput ${errors.moq ? 'cpInputError' : ''}`}
                            name="moq" value={formData.moq} onChange={handleInputChange}
                            placeholder="e.g. 50" min="1" />
                        {errors.moq && <span className="cpError">{errors.moq}</span>}
                    </div>
                    <div className="cpFormGroup">
                        <label className="cpLabel">Selling Scope</label>
                        <Dropdown
                            options={[
                                { value: 'local', label: 'Sell Locally (within your country)' },
                                { value: 'international', label: 'Sell Internationally (worldwide)' },
                            ]}
                            value={{ value: formData.sellingScope, label: formData.sellingScope === 'local' ? 'Sell Locally' : 'Sell Internationally' }}
                            onChange={(selected) => setFormData(prev => ({ ...prev, sellingScope: selected.value }))}
                            placeholder="Select selling scope" />
                    </div>
                </div>

                <div className="cpDivider" />

                <h3 className="cpSectionLabel">Product Specifications</h3>
                <p className="cpHelpText">Add details like Material, Weight, Dimensions, Color, etc.</p>
                <div className="cpDetailRows">
                    {formData.productDetails?.map((detail, index) => (
                        <div key={index} className="cpDetailRow">
                            <input type="text" className={`cpInput ${errors[`productDetailTitle_${index}`] ? 'cpInputError' : ''}`}
                                placeholder="Title (e.g., Material, Weight)" value={detail.title}
                                onChange={(e) => {
                                    const newDetails = [...formData.productDetails];
                                    newDetails[index].title = e.target.value;
                                    setFormData(prev => ({ ...prev, productDetails: newDetails }));
                                }} />
                            <input type="text" className={`cpInput cpDetailDesc ${errors[`productDetailDesc_${index}`] ? 'cpInputError' : ''}`}
                                placeholder="Description (e.g., Cotton, 500g)" value={detail.description}
                                onChange={(e) => {
                                    const newDetails = [...formData.productDetails];
                                    newDetails[index].description = e.target.value;
                                    setFormData(prev => ({ ...prev, productDetails: newDetails }));
                                }} />
                            {formData.productDetails.length > 1 && (
                                <button type="button" className="cpRemoveBtn" onClick={() => {
                                    setFormData(prev => ({ ...prev, productDetails: prev.productDetails.filter((_, i) => i !== index) }));
                                }}><Trash2 size={16} /></button>
                            )}
                        </div>
                    ))}
                    <button type="button" className="cpAddBtn" onClick={() => {
                        setFormData(prev => ({ ...prev, productDetails: [...prev.productDetails, { title: '', description: '' }] }));
                    }}><Plus size={16} /> Add Detail</button>
                    {errors.productDetails && <span className="cpError">{errors.productDetails}</span>}
                </div>

                <div className="cpDivider" />

                <h3 className="cpSectionLabel">Feature Table</h3>
                <p className="cpHelpText">Add key features or certifications in a table format (optional)</p>
                <div className="cpDetailRows">
                    {featureTable.map((feature, index) => (
                        <div key={index} className="cpDetailRow">
                            <input type="text" className="cpInput" placeholder="Label (e.g., Certification)"
                                value={feature.label} onChange={(e) => {
                                    const u = [...featureTable]; u[index].label = e.target.value; setFeatureTable(u);
                                }} />
                            <input type="text" className="cpInput cpDetailDesc" placeholder="Value (e.g., ISO 9001)"
                                value={feature.value} onChange={(e) => {
                                    const u = [...featureTable]; u[index].value = e.target.value; setFeatureTable(u);
                                }} />
                            {featureTable.length > 1 && (
                                <button type="button" className="cpRemoveBtn" onClick={() => setFeatureTable(featureTable.filter((_, i) => i !== index))}>
                                    <Trash2 size={16} />
                                </button>
                            )}
                        </div>
                    ))}
                    <button type="button" className="cpAddBtn" onClick={() => setFeatureTable([...featureTable, { label: '', value: '' }])}>
                        <Plus size={16} /> Add Feature
                    </button>
                </div>

                <div className="cpDivider" />

                <h3 className="cpSectionLabel">Product Images <span className="cpRequired">*</span></h3>
                <ImageUpload
                    onImagesChange={(imageData) => {
                        setFormData(prev => ({ ...prev, images: imageData.images, thumbnail: imageData.thumbnail }));
                    }}
                    initialImages={formData.images}
                    initialThumbnail={formData.thumbnail}
                    key={formData.id || 'new'}
                />
                {errors.thumbnail && <span className="cpError">{errors.thumbnail}</span>}
            </div>
        </div>
    );
};

// ─── B2B STEP 2: Pricing & Wholesale ────────────────────────────────
const B2BPricingStep = ({
    formData, errors,
    priceTiers, setPriceTiers,
    b2bVariations, setB2bVariations,
    customizationOptions, setCustomizationOptions,
    saleDiscount, setSaleDiscount,
    addedServices, setAddedServices,
    buyerQuestions, setBuyerQuestions,
    currency, exchangeRate,
    feesObj,
}) => {
    const feePercent = sellerFeePercentFor(feesObj?.sellerMatrix, {
        isB2B: true,
        listingType: formData.listingType,
    });
    const showWholesale = formData.listingMode === 'wholesale' || formData.listingMode === 'both';
    const showCustomization = formData.listingMode === 'customization' || formData.listingMode === 'both';
    const [newVarName, setNewVarName] = useState('');
    const [newVarOptions, setNewVarOptions] = useState([{ label: '', priceAdjust: '', included: false }]);
    const [showVarForm, setShowVarForm] = useState(false);
    const [editingB2bVarIdx, setEditingB2bVarIdx] = useState(null);

    const addPriceTier = () => setPriceTiers([...priceTiers, { minQty: '', price: '' }]);
    const removePriceTier = (i) => setPriceTiers(priceTiers.filter((_, idx) => idx !== i));
    const updatePriceTier = (i, field, value) => { const u = [...priceTiers]; u[i][field] = value; setPriceTiers(u); };

    const saveVariation = () => {
        if (!newVarName.trim()) return;
        const validOpts = newVarOptions.filter(o => o.label.trim());
        if (validOpts.length === 0) return;
        const variation = {
            name: newVarName.trim(),
            options: validOpts.map(o => ({ label: o.label.trim(), priceAdjust: o.included ? 0 : (parseFloat(o.priceAdjust) || 0) }))
        };
        if (editingB2bVarIdx !== null) {
            setB2bVariations(b2bVariations.map((v, i) => i === editingB2bVarIdx ? variation : v));
        } else {
            setB2bVariations([...b2bVariations, variation]);
        }
        setNewVarName(''); setNewVarOptions([{ label: '', priceAdjust: '', included: false }]); setShowVarForm(false); setEditingB2bVarIdx(null);
    };
    const removeVariation = (i) => setB2bVariations(b2bVariations.filter((_, idx) => idx !== i));
    const editVariation = (i) => {
        const v = b2bVariations[i];
        setEditingB2bVarIdx(i);
        setNewVarName(v.name);
        setNewVarOptions(v.options.map(o => ({ label: o.label, priceAdjust: o.priceAdjust === 0 ? '' : String(o.priceAdjust), included: o.priceAdjust === 0 })));
        setShowVarForm(true);
    };

    const addCustomOption = () => setCustomizationOptions([...customizationOptions, { description: '', moq: '', samplePrice: '', sampleShippingFee: '' }]);
    const removeCustomOption = (i) => setCustomizationOptions(customizationOptions.filter((_, idx) => idx !== i));
    const updateCustomOption = (i, field, value) => { const u = [...customizationOptions]; u[i][field] = value; setCustomizationOptions(u); };

    return (
        <>
            {showWholesale && (
                <div className="cpCard">
                    <div className="cpCardHeader">
                        <span className="cpCardIcon">💰</span>
                        <div>
                            <h2 className="cpCardTitle">Price Tiers</h2>
                            <p className="cpCardSubtitle">Set volume-based pricing for wholesale buyers</p>
                        </div>
                    </div>
                    <div className="cpCardBody">
                        <div className="cpInfoBanner">
                            <FaInfoCircle />
                            <p>Add different price points based on order quantity. Buyers see lower prices as they order more.</p>
                        </div>
                        {priceTiers.map((tier, i) => (
                            <div key={i} className="cpBulkRow">
                                <div className="cpFormGroup">
                                    <label className="cpLabel">Quantity from <span className="cpRequired">*</span></label>
                                    <input type="number" className={`cpInput ${errors[`priceTier_${i}_minQty`] ? 'cpInputError' : ''}`}
                                        value={tier.minQty} onChange={(e) => updatePriceTier(i, 'minQty', e.target.value)} placeholder="e.g. 50" min="1" />
                                    {errors[`priceTier_${i}_minQty`] && <span className="cpError">{errors[`priceTier_${i}_minQty`]}</span>}
                                </div>
                                <div className="cpFormGroup">
                                    <label className="cpLabel">Unit Price ($) <span className="cpRequired">*</span></label>
                                    <input type="number" className={`cpInput ${errors[`priceTier_${i}_price`] ? 'cpInputError' : ''}`}
                                        value={tier.price} onChange={(e) => updatePriceTier(i, 'price', e.target.value)} placeholder="e.g. 4.50" min="0" step="0.01" />
                                    {currency !== 'USD' && parseFloat(tier.price) > 0 && (
                                        <span className="cpLocalPreview">{formatUsdToLocal(tier.price, currency, exchangeRate)}</span>
                                    )}
                                    {errors[`priceTier_${i}_price`] && <span className="cpError">{errors[`priceTier_${i}_price`]}</span>}
                                </div>
                                {priceTiers.length > 1 && (
                                    <button className="cpRemoveBtn" onClick={() => removePriceTier(i)}><Trash2 size={16} /></button>
                                )}
                            </div>
                        ))}
                        <button className="cpAddBtn" onClick={addPriceTier}><Plus size={16} /> Add Price Tier</button>
                        {errors.priceTiers && <span className="cpError">{errors.priceTiers}</span>}
                    </div>
                </div>
            )}

            {feesObj?.seller != null && priceTiers.some(t => t.price && parseFloat(t.price) > 0) && (
                <div className="cpEarningsBanner">
                    <strong>You will receive per unit (after {feePercent}% service fee):</strong>
                    <span>
                        {priceTiers.filter(t => t.minQty && t.price && parseFloat(t.price) > 0).map((tier, i) => {
                            const amt = calculateSellerAmount(parseFloat(tier.price), feePercent);
                            return <span key={i}>{i > 0 ? ', ' : ''}{tier.minQty}+ units: {formatCurrencyDual(amt, currency, exchangeRate)}</span>;
                        })}
                    </span>
                    <PayoutFeeNote />
                </div>
            )}

            <div className="cpCard">
                <div className="cpCardHeader">
                    <span className="cpCardIcon">🎨</span>
                    <div>
                        <h2 className="cpCardTitle">Product Variations</h2>
                        <p className="cpCardSubtitle">Add variations like Color, Size, Material (optional)</p>
                    </div>
                </div>
                <div className="cpCardBody">
                    {b2bVariations.length > 0 && (
                        <div className="cpVariablesList">
                            {b2bVariations.map((v, i) => (
                                <div key={i} className="cpVariableCard">
                                    <div className="cpVariableCardHeader">
                                        <h4>{v.name}</h4>
                                        <div className="cpVariableCardActions">
                                            <button className="cpEditBtn" onClick={() => editVariation(i)}><Pencil size={15} /></button>
                                            <button className="cpRemoveBtn" onClick={() => removeVariation(i)}><Trash2 size={16} /></button>
                                        </div>
                                    </div>
                                    <div className="cpOptionsGrid">
                                        {v.options.map((opt, oi) => (
                                            <div key={oi} className="cpOptionChip">
                                                <span className="cpOptionName">{opt.label}</span>
                                                {opt.priceAdjust ? <span className="cpOptionPrice">+{formatCurrencyDual(opt.priceAdjust, currency, exchangeRate)}</span> : <span className="cpOptionIncluded">Included</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {showVarForm ? (
                        <div className="cpVariableFormInline">
                            <div className="cpFormGroup">
                                <label className="cpLabel">Variation Name</label>
                                <input type="text" className="cpInput" value={newVarName}
                                    onChange={(e) => setNewVarName(e.target.value)} placeholder="e.g. Color, Size" />
                            </div>
                            <div className="cpFormGroup">
                                <label className="cpLabel">Options</label>
                                {newVarOptions.map((opt, oi) => (
                                    <div key={oi} className="cpDetailRow cpVarOptRow" style={{ marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <input type="text" className="cpInput" placeholder="Option label (e.g. Red, Large)"
                                            value={opt.label} onChange={(e) => {
                                                const u = [...newVarOptions]; u[oi] = { ...u[oi], label: e.target.value }; setNewVarOptions(u);
                                            }} />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontSize: '13px', cursor: 'pointer', margin: '0 4px' }}>
                                            <input type="checkbox" checked={opt.included || false} onChange={(e) => {
                                                const u = [...newVarOptions]; u[oi] = { ...u[oi], included: e.target.checked, priceAdjust: e.target.checked ? '' : u[oi].priceAdjust }; setNewVarOptions(u);
                                            }} />
                                            Included
                                        </label>
                                        {!opt.included && (
                                            <input type="number" className="cpInput" placeholder="Price adjust (USD)"
                                                value={opt.priceAdjust} onChange={(e) => {
                                                    const u = [...newVarOptions]; u[oi] = { ...u[oi], priceAdjust: e.target.value }; setNewVarOptions(u);
                                                }} step="0.01" style={{ maxWidth: 140 }} />
                                        )}
                                        {currency !== 'USD' && !opt.included && parseFloat(opt.priceAdjust) > 0 && (
                                            <span className="cpLocalPreview">{formatUsdToLocal(opt.priceAdjust, currency, exchangeRate)}</span>
                                        )}
                                        {newVarOptions.length > 1 && (
                                            <button className="cpRemoveBtn" onClick={() => setNewVarOptions(newVarOptions.filter((_, idx) => idx !== oi))}>
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button className="cpAddBtn cpCompactBtn" onClick={() => setNewVarOptions([...newVarOptions, { label: '', priceAdjust: '', included: false }])}>
                                    <Plus size={14} /> Add Option
                                </button>
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <button className="cpNavBtn cpNavBtnNext" style={{ padding: '8px 16px', fontSize: 14 }} onClick={saveVariation}>{editingB2bVarIdx !== null ? 'Update Variation' : 'Save Variation'}</button>
                                <button className="cpNavBtn cpNavBtnBack" style={{ padding: '8px 16px', fontSize: 14 }} onClick={() => { setShowVarForm(false); setEditingB2bVarIdx(null); setNewVarName(''); setNewVarOptions([{ label: '', priceAdjust: '', included: false }]); }}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <button className="cpAddBtn" onClick={() => setShowVarForm(true)}><Plus size={16} /> Add Variation</button>
                    )}
                </div>
            </div>

            {showCustomization && (
                <div className="cpCard">
                    <div className="cpCardHeader">
                        <span className="cpCardIcon">🔧</span>
                        <div>
                            <h2 className="cpCardTitle">Customization Options</h2>
                            <p className="cpCardSubtitle">Define what customization services you offer</p>
                        </div>
                    </div>
                    <div className="cpCardBody">
                        <div className="cpInfoBanner">
                            <FaInfoCircle />
                            <p>Describe each customization option with its MOQ and sample pricing. Buyers can request samples before placing bulk orders.</p>
                        </div>
                        {customizationOptions.map((opt, i) => (
                            <div key={i} className="cpBulkRow" style={{ alignItems: 'flex-start' }}>
                                <div className="cpFormGroup" style={{ flex: 2 }}>
                                    <label className="cpLabel">Description <span className="cpRequired">*</span></label>
                                    <input type="text" className={`cpInput ${errors[`customOpt_${i}_desc`] ? 'cpInputError' : ''}`}
                                        value={opt.description} onChange={(e) => updateCustomOption(i, 'description', e.target.value)}
                                        placeholder="e.g. Custom logo printing" />
                                    {errors[`customOpt_${i}_desc`] && <span className="cpError">{errors[`customOpt_${i}_desc`]}</span>}
                                </div>
                                <div className="cpFormGroup">
                                    <label className="cpLabel">MOQ</label>
                                    <input type="number" className="cpInput" value={opt.moq}
                                        onChange={(e) => updateCustomOption(i, 'moq', e.target.value)} placeholder="Min units" min="1" />
                                </div>
                                <div className="cpFormGroup">
                                    <label className="cpLabel">Sample Price ($)</label>
                                    <input type="number" className="cpInput" value={opt.samplePrice}
                                        onChange={(e) => updateCustomOption(i, 'samplePrice', e.target.value)} placeholder="0.00" min="0" step="0.01" />
                                    {currency !== 'USD' && parseFloat(opt.samplePrice) > 0 && (
                                        <span className="cpLocalPreview">{formatUsdToLocal(opt.samplePrice, currency, exchangeRate)}</span>
                                    )}
                                </div>
                                <div className="cpFormGroup">
                                    <label className="cpLabel">Sample Shipping ($)</label>
                                    <input type="number" className="cpInput" value={opt.sampleShippingFee}
                                        onChange={(e) => updateCustomOption(i, 'sampleShippingFee', e.target.value)} placeholder="0.00" min="0" step="0.01" />
                                    {currency !== 'USD' && parseFloat(opt.sampleShippingFee) > 0 && (
                                        <span className="cpLocalPreview">{formatUsdToLocal(opt.sampleShippingFee, currency, exchangeRate)}</span>
                                    )}
                                </div>
                                <button className="cpRemoveBtn" onClick={() => removeCustomOption(i)} style={{ marginTop: 28 }}><Trash2 size={16} /></button>
                            </div>
                        ))}
                        <button className="cpAddBtn" onClick={addCustomOption}><Plus size={16} /> Add Customization Option</button>
                        {errors.customizationOptions && <span className="cpError">{errors.customizationOptions}</span>}
                    </div>
                </div>
            )}

            <div className="cpCard">
                <div className="cpCardHeader">
                    <span className="cpCardIcon">🏷️</span>
                    <div>
                        <h2 className="cpCardTitle">Discount</h2>
                        <p className="cpCardSubtitle">Attract more buyers with a sale discount</p>
                    </div>
                </div>
                <div className="cpCardBody">
                    <div className="cpFormGrid cpFormTriple">
                        <div className="cpFormGroup">
                            <label className="cpLabel">Discount Type</label>
                            <div className="cpToggleGroup">
                                <button className={`cpToggleBtn ${saleDiscount.type === 'percentage' ? 'active' : ''}`}
                                    onClick={() => setSaleDiscount(prev => ({ ...prev, type: 'percentage' }))}><FaPercent /> Percentage</button>
                                <button className={`cpToggleBtn ${saleDiscount.type === 'fixed' ? 'active' : ''}`}
                                    onClick={() => setSaleDiscount(prev => ({ ...prev, type: 'fixed' }))}><DollarSign size={14} /> Fixed ($)</button>
                            </div>
                        </div>
                        <div className="cpFormGroup">
                            <label className="cpLabel">{saleDiscount.type === 'percentage' ? 'Discount %' : 'Discount Amount ($)'}</label>
                            <input type="number" className="cpInput"
                                placeholder={saleDiscount.type === 'percentage' ? 'e.g. 15' : 'e.g. 5.00'}
                                value={saleDiscount.value} onChange={(e) => setSaleDiscount(prev => ({ ...prev, value: e.target.value }))}
                                min="0" max={saleDiscount.type === 'percentage' ? '100' : undefined} />
                            {saleDiscount.type === 'fixed' && currency !== 'USD' && parseFloat(saleDiscount.value) > 0 && (
                                <span className="cpLocalPreview">{formatUsdToLocal(saleDiscount.value, currency, exchangeRate)}</span>
                            )}
                        </div>
                        <div className="cpFormGroup">
                            <label className="cpLabel">Sale Ends On</label>
                            <input type="date" className="cpInput" value={saleDiscount.endDate}
                                onChange={(e) => setSaleDiscount(prev => ({ ...prev, endDate: e.target.value }))}
                                min={new Date().toISOString().split('T')[0]} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="cpCard">
                <div className="cpCardHeader">
                    <span className="cpCardIcon">✨</span>
                    <div>
                        <h2 className="cpCardTitle">Added Services</h2>
                        <p className="cpCardSubtitle">Offer extra services like quality inspection, special packing, etc.</p>
                    </div>
                </div>
                <div className="cpCardBody">
                    {addedServices.length > 0 && (
                        <div className="cpAddedServicesList">
                            {addedServices.map((svc, index) => (
                                <div key={index} className="cpAddedServiceCard">
                                    <div className="cpAddedServiceMain">
                                        <div className="cpFormGroup">
                                            <label className="cpLabel">Service Name <span className="cpRequired">*</span></label>
                                            <input type="text" className="cpInput" placeholder="e.g. Quality Inspection"
                                                value={svc.name} onChange={(e) => {
                                                    const u = [...addedServices]; u[index].name = e.target.value; setAddedServices(u);
                                                }} />
                                        </div>
                                        <div className="cpFormGroup">
                                            <label className="cpLabel">Description</label>
                                            <input type="text" className="cpInput" placeholder="Brief description"
                                                value={svc.description} onChange={(e) => {
                                                    const u = [...addedServices]; u[index].description = e.target.value; setAddedServices(u);
                                                }} />
                                        </div>
                                        <div className="cpFormGroup cpFormSmall">
                                            <label className="cpLabel">Price ($)</label>
                                            <input type="number" className="cpInput" placeholder="0 = Free"
                                                value={svc.price} onChange={(e) => {
                                                    const u = [...addedServices]; u[index].price = e.target.value; setAddedServices(u);
                                                }} min="0" step="0.01" />
                                            {currency !== 'USD' && parseFloat(svc.price) > 0 && (
                                                <span className="cpLocalPreview">{formatUsdToLocal(svc.price, currency, exchangeRate)}</span>
                                            )}
                                        </div>
                                        <div className="cpFormGroup cpFormSmall">
                                            <label className="cpLabel">Pricing Mode</label>
                                            <select
                                                className="cpSelect"
                                                value={svc.pricingMode || 'perOrder'}
                                                onChange={(e) => {
                                                    const u = [...addedServices];
                                                    u[index].pricingMode = e.target.value;
                                                    setAddedServices(u);
                                                }}
                                            >
                                                <option value="perOrder">Per Order</option>
                                                <option value="perUnit">Per Unit</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="cpAddedServiceActions">
                                        <button className="cpRemoveBtn" onClick={() => setAddedServices(addedServices.filter((_, i) => i !== index))}><Trash2 size={16} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <button className="cpAddBtn" onClick={() => setAddedServices([...addedServices, { name: '', description: '', price: '' }])}>
                        <Plus size={16} /> Add Service
                    </button>
                </div>
            </div>

            <div className="cpCard">
                <div className="cpCardHeader">
                    <span className="cpCardIcon">❓</span>
                    <div>
                        <h2 className="cpCardTitle">Buyer Requirements</h2>
                        <p className="cpCardSubtitle">Ask buyers questions they must answer before ordering</p>
                    </div>
                </div>
                <div className="cpCardBody">
                    {buyerQuestions.length > 0 && (
                        <div className="cpAddedServicesList">
                            {buyerQuestions.map((q, index) => (
                                <div key={index} className="cpAddedServiceCard">
                                    <div className="cpAddedServiceMain" style={{ flex: 1 }}>
                                        <div className="cpFormGroup">
                                            <label className="cpLabel">Question <span className="cpRequired">*</span></label>
                                            <input type="text" className="cpInput" placeholder="e.g. What is your desired packaging?"
                                                value={q.question} onChange={(e) => {
                                                    const u = [...buyerQuestions]; u[index].question = e.target.value; setBuyerQuestions(u);
                                                }} />
                                        </div>
                                    </div>
                                    <div className="cpAddedServiceActions">
                                        <button className="cpRemoveBtn" onClick={() => setBuyerQuestions(buyerQuestions.filter((_, i) => i !== index))}><Trash2 size={16} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <button className="cpAddBtn" onClick={() => setBuyerQuestions([...buyerQuestions, { question: '' }])}>
                        <Plus size={16} /> Add Question
                    </button>
                </div>
            </div>
        </>
    );
};

// ─── B2B STEP 3: Shipping & Services ────────────────────────────────
const B2BShippingExtrasStep = ({ formData, handleInputChange, errors, setFormData, currency, exchangeRate }) => {
    const isInternational = formData.sellingScope === 'international';

    return (
        <>
            <div className="cpCard">
                <div className="cpCardHeader">
                    <span className="cpCardIcon">🚚</span>
                    <div>
                        <h2 className="cpCardTitle">Shipping</h2>
                        <p className="cpCardSubtitle">Set shipping fees based on buyer location</p>
                    </div>
                </div>
                <div className="cpCardBody">
                    <h3 className="cpSectionLabel">Shipping Policy</h3>
                    <div className="cpInfoBanner">
                        <FaInfoCircle />
                        <p>Choose how shipping fees are calculated for buyers' orders.</p>
                    </div>

                    {/* Shipping method selector */}
                    <div className="cpFormGroup">
                        <label className="cpLabel">Shipping Fee Calculation Method <span className="cpRequired">*</span></label>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
                            {[
                                { value: 'byOrder', label: '📦 Flat Fee (per order)', desc: 'A single fixed fee regardless of quantity' },
                                { value: 'byUnit', label: '📏 Per Unit', desc: 'Fee multiplied by the number of units ordered' },
                                { value: 'byMOQ', label: '📊 MOQ Based', desc: 'Flat fee up to MOQ, then per-unit above MOQ' },
                            ].map(opt => (
                                <label key={opt.value} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 16px',
                                    border: `2px solid ${formData.shippingPolicy?.type === opt.value ? '#4CAF50' : '#e0e0e0'}`,
                                    borderRadius: '10px', cursor: 'pointer', flex: '1', minWidth: '180px',
                                    background: formData.shippingPolicy?.type === opt.value ? '#f0fff0' : '#fff',
                                    transition: 'all 0.2s',
                                }}>
                                    <input type="radio" name="b2bShippingPolicyType" value={opt.value}
                                        checked={formData.shippingPolicy?.type === opt.value}
                                        onChange={() => setFormData(prev => ({
                                            ...prev,
                                            shippingPolicy: { ...prev.shippingPolicy, type: opt.value }
                                        }))}
                                        style={{ marginTop: '2px', accentColor: '#4CAF50' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>{opt.label}</div>
                                        <div style={{ fontSize: '12px', color: '#777', marginTop: '2px' }}>{opt.desc}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {isInternational ? (
                        <div className="cpShippingTiers" style={{ marginTop: '16px' }}>
                            {[
                                { key: 'nearby', label: 'Within Region / Nearby', icon: '📍' },
                                { key: 'withinCountry', label: 'Within Country', icon: '🏠' },
                                { key: 'worldwide', label: 'Worldwide', icon: '🌍' },
                            ].map(tier => {
                                const regionData = formData.shippingPolicy?.regions?.[tier.key] || {};
                                const policyType = formData.shippingPolicy?.type || 'byOrder';
                                const updateRegion = (field, value) => setFormData(prev => ({
                                    ...prev,
                                    shippingPolicy: {
                                        ...prev.shippingPolicy,
                                        regions: {
                                            ...prev.shippingPolicy?.regions,
                                            [tier.key]: { ...prev.shippingPolicy?.regions?.[tier.key], [field]: value }
                                        }
                                    }
                                }));
                                return (
                                    <div key={tier.key} className="cpShippingTier">
                                        <div className="cpShippingTierHeader">
                                            <span>{tier.icon} {tier.label}</span>
                                            <label className="cpCheckboxLabel cpFreeLabel" style={{ marginLeft: 'auto' }}>
                                                <input type="checkbox" className="cpCheckbox"
                                                    checked={regionData.free || false}
                                                    onChange={(e) => updateRegion('free', e.target.checked)}
                                                />
                                                Free Shipping
                                            </label>
                                        </div>
                                        <div className="cpShippingTierBody">
                                            {!regionData.free && (
                                                <>
                                                    {policyType === 'byOrder' && (
                                                        <div className="cpFeeRow">
                                                            <input type="number" min="0" step="0.01"
                                                                value={regionData.flatFee ?? ''}
                                                                onChange={(e) => updateRegion('flatFee', e.target.value)}
                                                                className={`cpInput ${errors[`shipping_${tier.key}`] ? 'cpInputError' : ''}`}
                                                                placeholder="Flat fee (USD)"
                                                            />
                                                        </div>
                                                    )}
                                                    {policyType === 'byUnit' && (
                                                        <div className="cpFeeRow">
                                                            <input type="number" min="0" step="0.01"
                                                                value={regionData.perUnitFee ?? ''}
                                                                onChange={(e) => updateRegion('perUnitFee', e.target.value)}
                                                                className={`cpInput ${errors[`shipping_${tier.key}`] ? 'cpInputError' : ''}`}
                                                                placeholder="Fee per unit (USD)"
                                                            />
                                                        </div>
                                                    )}
                                                    {policyType === 'byMOQ' && (
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                            <div className="cpFormGroup" style={{ flex: '1', minWidth: '120px' }}>
                                                                <label className="cpLabel cpSmallLabel">MOQ Qty</label>
                                                                <input type="number" min="1" step="1"
                                                                    value={regionData.moqQty ?? ''}
                                                                    onChange={(e) => updateRegion('moqQty', e.target.value)}
                                                                    className={`cpInput ${errors[`shipping_${tier.key}`] ? 'cpInputError' : ''}`}
                                                                    placeholder="e.g. 50"
                                                                />
                                                            </div>
                                                            <div className="cpFormGroup" style={{ flex: '1', minWidth: '120px' }}>
                                                                <label className="cpLabel cpSmallLabel">Flat fee up to MOQ ($)</label>
                                                                <input type="number" min="0" step="0.01"
                                                                    value={regionData.moqFlatFee ?? ''}
                                                                    onChange={(e) => updateRegion('moqFlatFee', e.target.value)}
                                                                    className="cpInput" placeholder="e.g. 20"
                                                                />
                                                            </div>
                                                            <div className="cpFormGroup" style={{ flex: '1', minWidth: '120px' }}>
                                                                <label className="cpLabel cpSmallLabel">Per unit above MOQ ($)</label>
                                                                <input type="number" min="0" step="0.01"
                                                                    value={regionData.moqPerUnitAbove ?? ''}
                                                                    onChange={(e) => updateRegion('moqPerUnitAbove', e.target.value)}
                                                                    className="cpInput" placeholder="e.g. 0.50"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            <div className="cpFormGroup cpCompact">
                                                <label className="cpLabel cpSmallLabel">Est. delivery days</label>
                                                <input type="text"
                                                    value={formData.estimatedDeliveryDays?.[tier.key] || ''}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        estimatedDeliveryDays: { ...prev.estimatedDeliveryDays, [tier.key]: e.target.value }
                                                    }))}
                                                    className="cpInput" placeholder="e.g. 5-10 days"
                                                />
                                            </div>
                                            {errors[`shipping_${tier.key}`] && <span className="cpError">{errors[`shipping_${tier.key}`]}</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{ marginTop: '16px' }}>
                            <label className="cpCheckboxLabel cpFreeLabel" style={{ marginBottom: '12px', display: 'inline-flex' }}>
                                <input type="checkbox" className="cpCheckbox"
                                    checked={formData.shippingPolicy?.free || false}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        shippingPolicy: { ...prev.shippingPolicy, free: e.target.checked }
                                    }))}
                                />
                                Free Shipping
                            </label>

                            {!formData.shippingPolicy?.free && (
                                <>
                                    {formData.shippingPolicy?.type === 'byOrder' && (
                                        <div className="cpFormGroup">
                                            <label className="cpLabel">Flat Shipping Fee (USD)</label>
                                            <input type="number" min="0" step="0.01"
                                                value={formData.shippingPolicy?.flatFee ?? ''}
                                                onChange={e => setFormData(prev => ({
                                                    ...prev,
                                                    shippingPolicy: { ...prev.shippingPolicy, flatFee: e.target.value }
                                                }))}
                                                className={`cpInput ${errors.shippingPolicy ? 'cpInputError' : ''}`}
                                                placeholder="e.g. 5.00" style={{ maxWidth: '240px' }}
                                            />
                                        </div>
                                    )}
                                    {formData.shippingPolicy?.type === 'byUnit' && (
                                        <div className="cpFormGroup">
                                            <label className="cpLabel">Shipping Fee per Unit (USD)</label>
                                            <input type="number" min="0" step="0.01"
                                                value={formData.shippingPolicy?.perUnitFee ?? ''}
                                                onChange={e => setFormData(prev => ({
                                                    ...prev,
                                                    shippingPolicy: { ...prev.shippingPolicy, perUnitFee: e.target.value }
                                                }))}
                                                className={`cpInput ${errors.shippingPolicy ? 'cpInputError' : ''}`}
                                                placeholder="e.g. 1.50" style={{ maxWidth: '240px' }}
                                            />
                                        </div>
                                    )}
                                    {formData.shippingPolicy?.type === 'byMOQ' && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                            <div className="cpFormGroup" style={{ flex: '1', minWidth: '160px' }}>
                                                <label className="cpLabel">MOQ Quantity</label>
                                                <input type="number" min="1" step="1"
                                                    value={formData.shippingPolicy?.moqQty ?? ''}
                                                    onChange={e => setFormData(prev => ({
                                                        ...prev,
                                                        shippingPolicy: { ...prev.shippingPolicy, moqQty: e.target.value }
                                                    }))}
                                                    className={`cpInput ${errors.shippingPolicy ? 'cpInputError' : ''}`}
                                                    placeholder="e.g. 50"
                                                />
                                            </div>
                                            <div className="cpFormGroup" style={{ flex: '1', minWidth: '160px' }}>
                                                <label className="cpLabel">Flat Fee up to MOQ (USD)</label>
                                                <input type="number" min="0" step="0.01"
                                                    value={formData.shippingPolicy?.moqFlatFee ?? ''}
                                                    onChange={e => setFormData(prev => ({
                                                        ...prev,
                                                        shippingPolicy: { ...prev.shippingPolicy, moqFlatFee: e.target.value }
                                                    }))}
                                                    className="cpInput" placeholder="e.g. 20.00"
                                                />
                                            </div>
                                            <div className="cpFormGroup" style={{ flex: '1', minWidth: '160px' }}>
                                                <label className="cpLabel">Per Unit Above MOQ (USD)</label>
                                                <input type="number" min="0" step="0.01"
                                                    value={formData.shippingPolicy?.moqPerUnitAbove ?? ''}
                                                    onChange={e => setFormData(prev => ({
                                                        ...prev,
                                                        shippingPolicy: { ...prev.shippingPolicy, moqPerUnitAbove: e.target.value }
                                                    }))}
                                                    className="cpInput" placeholder="e.g. 0.50"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {errors.shippingPolicy && <span className="cpError">{errors.shippingPolicy}</span>}
                            <div className="cpFormGroup cpCompact" style={{ marginTop: '12px' }}>
                                <label className="cpLabel cpSmallLabel">Est. delivery days</label>
                                <input
                                    type="text"
                                    value={formData.shippingTimeDays || ''}
                                    onChange={handleInputChange}
                                    name="shippingTimeDays"
                                    className="cpInput"
                                    placeholder="e.g. 3-7 days"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="cpCard">
                <div className="cpCardHeader">
                    <span className="cpCardIcon">📜</span>
                    <div>
                        <h2 className="cpCardTitle">Cancellation Policy</h2>
                        <p className="cpCardSubtitle">Set your order cancellation rules</p>
                    </div>
                </div>
                <div className="cpCardBody">
                    <label className="cpLabel">Can order be cancelled?</label>
                    <div className="cpToggleGroup">
                        <button className={`cpToggleBtn ${formData.cancellable === 'yes' ? 'active' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'cancellable', value: 'yes' } })}>Yes</button>
                        <button className={`cpToggleBtn ${formData.cancellable === 'no' ? 'active' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'cancellable', value: 'no' } })}>No</button>
                    </div>
                    {formData.cancellable === 'no' && (
                        <div className="cpInfoBanner cpWarningBanner">
                            <FaInfoCircle />
                            <p>If set to 'No', no refund will be given to the buyer after placing the order.</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────
const CreateProductPageForm = ({ isB2B = false }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user, feesObj, currency, exchangeRate } = useContext(LoginContext);
    const { categories } = useContext(CategoryContext);
    const sellerCountry = user?.sellerId?.sellerAddress?.country?.name || '';
    const isEditing = Boolean(id && id !== 'new');
    const [currentStep, setCurrentStep] = useState(0);
    const [activeTab, setActiveTab] = useState('withoutVariable');
    const [basePrice, setBasePrice] = useState('');
    const [variables, setVariables] = useState([]);
    const [deliveryType, setDeliveryType] = useState([]);
    const [discounts, setDiscounts] = useState([]);
    const [errors, setErrors] = useState({});
    const presetVariables = ['Shape', 'Size', 'Color'];
    const [packages, setPackages] = useState([]);
    const [enabledPackages, setEnabledPackages] = useState(['Basic']);
    const [isLoading, setIsLoading] = useState(false);
    const [addedServices, setAddedServices] = useState([]);
    const [saleDiscount, setSaleDiscount] = useState({ type: 'percentage', value: '', endDate: '' });
    const [bulkPricing, setBulkPricing] = useState([]);
    const [buyerQuestions, setBuyerQuestions] = useState([]);
    const [b2bCategories, setB2bCategories] = useState([]);
    const [priceTiers, setPriceTiers] = useState([{ minQty: '', price: '' }]);
    const [b2bVariations, setB2bVariations] = useState([]);
    const [customizationOptions, setCustomizationOptions] = useState([]);
    const [featureTable, setFeatureTable] = useState([{ label: '', value: '' }]);

    const [formData, setFormData] = useState({
        productTitle: '',
        productDescription: '',
        ingredients: '',
        allergyInfo: '',
        productDetails: [{ title: '', description: '' }],
        briefDescription: '',
        cancellable: 'yes',
        sellingScope: 'local',
        serviceArea: 'country',
        serviceStates: [],
        thumbnail: null,
        pickupLocation: {
            country: { name: '', code: '' },
            state: { name: '', tax: 0 },
            city: '',
            address: '',
            zipCode: '',
        },
        estimatedDeliveryDays: { nearby: '', withinCountry: '', worldwide: '' },
        serviceMode: '',
        timeToDeliver: '',
        timeframe: '',
        maincategory: '',
        subcategory: '',
        childcategory: '',
        images: [],
        stock: null,
        listingMode: '',
        listingType: 'product',
        moq: '',
        productionTimeDays: '',
        shippingTimeDays: '',
        shippingPolicy: {
            type: 'byOrder', flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false,
            regions: {
                nearby: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                withinCountry: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                worldwide: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
            }
        },
    });
    const selectedCategory = categories.find(cat => cat.name === formData.maincategory);
    const isService = selectedCategory?.type === 'service';

    useEffect(() => { window.scrollTo(0, 0); }, [currentStep]);

    const scrollToFirstStepError = useCallback(() => {
        window.requestAnimationFrame(() => {
            const firstErrorEl = document.querySelector('.cpStepContent .cpInputError, .cpStepContent .cpError');
            if (!firstErrorEl) return;

            firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

            const focusTarget =
                firstErrorEl.matches('input, textarea, select, button')
                    ? firstErrorEl
                    : firstErrorEl.closest('.cpFormGroup, .cpDetailRow, .cpTimeRow, .cpPackagesRow, .cpVariableCard')
                        ?.querySelector('input, textarea, select, button');

            focusTarget?.focus?.({ preventScroll: true });
        });
    }, []);

    useEffect(() => {
        if (isB2B) {
            axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/categories?b2b=true`)
                .then(res => setB2bCategories(res.data.categories || []))
                .catch(err => console.error('Error fetching B2B categories:', err));
        }
    }, [isB2B]);

    useEffect(() => {
        if (isB2B && isEditing) {
            const fetchB2BListing = async () => {
                try {
                    const token = localStorage.getItem('token');
                    if (!token) { showToast("Please log in", "error"); navigate('/login'); return; }
                    setIsLoading(true);
                    const response = await axios.get(
                        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/seller/listings/${id}/edit`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    );
                    if (response.data.success) {
                        const listing = response.data.listing;
                        setFormData({
                            productTitle: listing.title || '',
                            productDescription: listing.description || '',
                            maincategory: listing.maincategory || '',
                            subcategory: listing.subcategory || '',
                            childcategory: listing.childcategory || '',
                            listingType: listing.listingType || 'product',
                            listingMode: listing.listingMode || '',
                            moq: listing.moq || '',
                            sellingScope: listing.sellingScope || 'international',
                            productDetails: listing.productDetails?.length ? listing.productDetails : [{ title: '', description: '' }],
                            thumbnail: listing.thumbnail || null,
                            images: listing.images?.map(url => ({ url, isExisting: true })) || [],
                            cancellable: listing.cancellable || 'yes',
                            productionTimeDays: listing.productionTimeDays || '',
                            shippingTimeDays: listing.shippingTimeDays || '',
                            ingredients: '', allergyInfo: '', briefDescription: '',
                            serviceArea: 'country', serviceStates: [],
                            pickupLocation: { country: { name: '', code: '' }, state: { name: '', tax: 0 }, city: '', address: '', zipCode: '' },
                            estimatedDeliveryDays: listing.estimatedDeliveryDays || { nearby: '', withinCountry: '', worldwide: '' },
                            serviceMode: '', timeToDeliver: '', timeframe: '', stock: null,
                            shippingPolicy: listing.shippingPolicy || {
                                type: 'byOrder', flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false,
                                regions: {
                                    nearby: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                                    withinCountry: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                                    worldwide: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                                }
                            },
                        });
                        setPriceTiers(listing.priceTiers?.length ? listing.priceTiers.map(t => ({
                            minQty: t.minQty ?? '', price: t.price ?? ''
                        })) : [{ minQty: '', price: '' }]);
                        setB2bVariations(listing.variations || []);
                        setCustomizationOptions(listing.customizationOptions || []);
                        setFeatureTable(listing.featureTable?.length ? listing.featureTable : [{ label: '', value: '' }]);
                        setSaleDiscount(listing.saleDiscount?.value ? {
                            type: listing.saleDiscount.type || 'percentage',
                            value: listing.saleDiscount.value || '',
                            endDate: listing.saleDiscount.endDate ? listing.saleDiscount.endDate.split('T')[0] : '',
                        } : { type: 'percentage', value: '', endDate: '' });
                        setAddedServices(listing.addedServices?.length ? listing.addedServices : []);
                        setBuyerQuestions(listing.buyerQuestions?.length ? listing.buyerQuestions : []);
                    } else {
                        showToast('Listing not found', 'error');
                        navigate('/postings');
                    }
                } catch (error) {
                    console.error('Error fetching B2B listing:', error);
                    showToast("Error fetching listing data", "error");
                    navigate('/postings');
                } finally { setIsLoading(false); }
            };
            fetchB2BListing();
        } else if (isB2B && !isEditing) {
            setFormData({
                productTitle: '', productDescription: '', ingredients: '', allergyInfo: '',
                productDetails: [{ title: '', description: '' }], briefDescription: '',
                cancellable: 'yes', sellingScope: 'international',
                serviceArea: 'country', serviceStates: [], thumbnail: null,
                pickupLocation: { country: { name: '', code: '' }, state: { name: '', tax: 0 }, city: '', address: '', zipCode: '' },
                estimatedDeliveryDays: { nearby: '', withinCountry: '', worldwide: '' },
                serviceMode: '', timeToDeliver: '', timeframe: '',
                maincategory: '', subcategory: '', childcategory: '', images: [], stock: null,
                listingMode: '', listingType: 'product', moq: '', productionTimeDays: '', shippingTimeDays: '',
                shippingPolicy: {
                    type: 'byOrder', flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false,
                    regions: {
                        nearby: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                        withinCountry: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                        worldwide: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                    }
                },
            });
            setPriceTiers([{ minQty: '', price: '' }]); setB2bVariations([]);
            setCustomizationOptions([]);
            setFeatureTable([{ label: '', value: '' }]);
            setSaleDiscount({ type: 'percentage', value: '', endDate: '' });
            setAddedServices([]); setBuyerQuestions([]);
        }
    }, [id, isB2B, isEditing, navigate]);

    useEffect(() => {
        if (isB2B) return;
        if (isEditing) {
            const fetchProduct = async () => {
                try {
                    const token = localStorage.getItem('token');
                    if (!token) { showToast("Please log in to edit a product", "error"); navigate('/login'); return; }
                    setIsLoading(true);
                    const response = await axios.get(
                        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/seller/product/${id}`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    );
                    if (response.data.success) {
                        const product = response.data.product;
                        setFormData({
                            productTitle: product.title || '',
                            productDescription: product.description || '',
                            ingredients: product.ingredients || '',
                            allergyInfo: product.allergyInfo || '',
                            productDetails: product.productDetails?.length ? product.productDetails : [{ title: '', description: '' }],
                            briefDescription: product.variablePricing?.briefDescription || '',
                            cancellable: product.cancellable || 'yes',
                            sellingScope: product.sellingScope || 'local',
                            serviceArea: product.serviceArea || 'country',
                            serviceStates: product.serviceStates || [],
                            thumbnail: product.thumbnail || null,
                            images: product.images.map(url => ({ url, isExisting: true })) || [],
                            pickupLocation: product.pickupLocation || { country: { name: '', code: '' }, state: { name: '', tax: 0 }, city: '', address: '', zipCode: '' },
                            estimatedDeliveryDays: product.estimatedDeliveryDays || { nearby: '', withinCountry: '', worldwide: '' },
                            serviceMode: product.serviceMode || '',
                            timeToDeliver: product.variablePricing?.timeToDeliver || '',
                            timeframe: product.variablePricing?.timeframe || '',
                            maincategory: product.maincategory || '',
                            subcategory: product.subcategory || '',
                            childcategory: product.childcategory || '',
                            stock: product.stock != null ? product.stock : null,
                            shippingPolicy: product.shippingPolicy || {
                                type: 'byOrder', flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false,
                                regions: {
                                    nearby: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                                    withinCountry: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                                    worldwide: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                                }
                            },
                        });
                        setBasePrice(product.variablePricing?.basePrice ? String(product.variablePricing.basePrice) : '');
                        setVariables(product.variablePricing?.variables || []);
                        setDeliveryType(product.deliveryType || []);
                        setDiscounts(product.discounts?.length > 0 ? product.discounts : []);
                        setSaleDiscount(product.saleDiscount ? {
                            type: product.saleDiscount.type || 'percentage',
                            value: product.saleDiscount.value || '',
                            endDate: product.saleDiscount.endDate ? product.saleDiscount.endDate.split('T')[0] : '',
                        } : { type: 'percentage', value: '', endDate: '' });
                        setBulkPricing(product.bulkPricing?.length > 0 ? product.bulkPricing : []);
                        setAddedServices(product.addedServices?.length > 0 ? product.addedServices : []);
                        setBuyerQuestions(product.buyerQuestions?.length > 0 ? product.buyerQuestions : []);
                        const mergedPackages = DEFAULT_PACKAGES.map(def => {
                            const existing = product.packages?.find(p => p.name === def.name);
                            return existing || { ...def };
                        });
                        setPackages(mergedPackages);
                        const enabled = mergedPackages.filter(p => p.subtitle || p.options?.basePrice > 0).map(p => p.name);
                        setEnabledPackages(enabled.length > 0 ? enabled : ['Basic']);
                        setActiveTab(product.pricingType === 'variable' ? 'variable' : 'withoutVariable');
                    } else {
                        showToast('Product not found', 'error');
                        navigate('/postings');
                    }
                } catch (error) {
                    console.error('Error fetching product:', error);
                    showToast("Error fetching product data", "error");
                    navigate('/postings');
                } finally { setIsLoading(false); }
            };
            fetchProduct();
        } else {
            setFormData({
                productTitle: '', productDescription: '', ingredients: '', allergyInfo: '',
                productDetails: [{ title: '', description: '' }], briefDescription: '',
                cancellable: 'yes', sellingScope: 'local',
                serviceArea: 'country', serviceStates: [], thumbnail: null,
                pickupLocation: { country: { name: '', code: '' }, state: { name: '', tax: 0 }, city: '', address: '', zipCode: '' },
                estimatedDeliveryDays: { nearby: '', withinCountry: '', worldwide: '' },
                serviceMode: '',
                timeToDeliver: '', timeframe: '', maincategory: '', subcategory: '', childcategory: '', images: [], stock: null,
                listingMode: '', listingType: 'product', moq: '', productionTimeDays: '', shippingTimeDays: '',
                shippingPolicy: {
                    type: 'byOrder', flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false,
                    regions: {
                        nearby: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                        withinCountry: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                        worldwide: { flatFee: '', perUnitFee: '', moqFlatFee: '', moqQty: '', moqPerUnitAbove: '', free: false },
                    }
                },
            });
            setBasePrice(''); setVariables([]); setDeliveryType([]); setDiscounts([]);
            setPackages([...DEFAULT_PACKAGES]); setEnabledPackages(['Basic']); setActiveTab('withoutVariable');
            setSaleDiscount({ type: 'percentage', value: '', endDate: '' }); setBulkPricing([]); setAddedServices([]); setBuyerQuestions([]);
        }
    }, [id, isEditing, navigate]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    }, []);

    const validateStep = useCallback((step) => {
        const newErrors = {};
        if (isB2B) {
            if (step === 0) {
                if (!formData.productTitle.trim()) newErrors.productTitle = 'Listing title is required';
                if (!formData.productDescription.trim()) newErrors.productDescription = 'Listing description is required';
                if (formData.productDescription.length > 1200) newErrors.productDescription = 'Description must not exceed 1200 characters';
                if (!formData.maincategory) newErrors.maincategory = 'Main category is required';
                if (!formData.subcategory) newErrors.subcategory = 'Sub category is required';
                if (!formData.listingMode) newErrors.listingMode = 'Listing mode is required';
                if (!formData.moq || parseInt(formData.moq) < 1) newErrors.moq = 'MOQ must be at least 1';
                if (!formData.thumbnail) newErrors.thumbnail = 'At least one product image is required';
                let hasValidDetail = false;
                formData.productDetails?.forEach((detail, index) => {
                    if (detail.title.trim() || detail.description.trim()) {
                        if (!detail.title.trim()) newErrors[`productDetailTitle_${index}`] = 'Title is required';
                        if (!detail.description.trim()) newErrors[`productDetailDesc_${index}`] = 'Description is required';
                        if (detail.title.trim() && detail.description.trim()) hasValidDetail = true;
                    }
                });
                if (!hasValidDetail) newErrors.productDetails = 'At least one product specification is required';
            } else if (step === 1) {
                const isWholesale = formData.listingMode === 'wholesale' || formData.listingMode === 'both';
                const isCustom = formData.listingMode === 'customization' || formData.listingMode === 'both';
                if (isWholesale) {
                    const filled = priceTiers.filter(t => t.minQty || t.price);
                    if (filled.length === 0) newErrors.priceTiers = 'At least one price tier is required for wholesale';
                    filled.forEach((t, i) => {
                        if (!t.minQty || parseInt(t.minQty) < 1) newErrors[`priceTier_${i}_minQty`] = 'Quantity is required';
                        else if (formData.moq && parseInt(t.minQty) < parseInt(formData.moq)) newErrors[`priceTier_${i}_minQty`] = `Must be ≥ MOQ (${formData.moq})`;
                        if (!t.price || parseFloat(t.price) < 5) newErrors[`priceTier_${i}_price`] = 'Price must be at least USD 5';
                    });
                }
                if (isCustom) {
                    const filled = customizationOptions.filter(o => o.description?.trim());
                    if (filled.length === 0) newErrors.customizationOptions = 'At least one customization option is required';
                    filled.forEach((o, i) => {
                        if (!o.description?.trim()) newErrors[`customOpt_${i}_desc`] = 'Description is required';
                    });
                }
            } else if (step === 2) {
                if (formData.sellingScope === 'international') {
                    const pType = formData.shippingPolicy?.type || 'byOrder';
                    ['nearby', 'withinCountry', 'worldwide'].forEach(key => {
                        const r = formData.shippingPolicy?.regions?.[key] || {};
                        if (r.free) return;
                        if (pType === 'byOrder' && (r.flatFee === '' || r.flatFee === undefined)) {
                            newErrors[`shipping_${key}`] = `Shipping fee for ${key.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`;
                        } else if (pType === 'byUnit' && (r.perUnitFee === '' || r.perUnitFee === undefined)) {
                            newErrors[`shipping_${key}`] = `Per-unit fee for ${key.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`;
                        } else if (pType === 'byMOQ' && (r.moqFlatFee === '' || r.moqFlatFee === undefined || r.moqQty === '' || r.moqQty === undefined)) {
                            newErrors[`shipping_${key}`] = `MOQ shipping values for ${key.replace(/([A-Z])/g, ' $1').toLowerCase()} are required`;
                        }
                    });
                } else if (!formData.shippingPolicy?.free) {
                    const pType = formData.shippingPolicy?.type || 'byOrder';
                    if (pType === 'byOrder' && (formData.shippingPolicy?.flatFee === '' || formData.shippingPolicy?.flatFee === undefined)) {
                        newErrors.shippingPolicy = 'Flat shipping fee is required';
                    } else if (pType === 'byUnit' && (formData.shippingPolicy?.perUnitFee === '' || formData.shippingPolicy?.perUnitFee === undefined)) {
                        newErrors.shippingPolicy = 'Per-unit shipping fee is required';
                    } else if (pType === 'byMOQ' && (formData.shippingPolicy?.moqFlatFee === '' || formData.shippingPolicy?.moqQty === '')) {
                        newErrors.shippingPolicy = 'MOQ shipping values are required';
                    }
                }
            }
        } else {
            if (step === 0) {
                if (!formData.productTitle.trim()) newErrors.productTitle = 'Product title is required';
                if (!formData.productDescription.trim()) newErrors.productDescription = 'Product description is required';
                if (formData.productDescription.length > 1200) newErrors.productDescription = 'Description must not exceed 1200 characters';
                if (formData.maincategory === 'Food, Sweets & Drinks') {
                    if (!formData.ingredients || !formData.ingredients.trim()) newErrors.ingredients = 'Ingredients are required';
                } else {
                    let hasValidDetail = false;
                    formData.productDetails?.forEach((detail, index) => {
                        if (detail.title.trim() || detail.description.trim()) {
                            if (!detail.title.trim()) newErrors[`productDetailTitle_${index}`] = 'Title is required';
                            if (!detail.description.trim()) newErrors[`productDetailDesc_${index}`] = 'Description is required';
                            if (detail.title.trim() && detail.description.trim()) hasValidDetail = true;
                        }
                    });
                    if (!hasValidDetail) newErrors.productDetails = 'At least one product detail is required';
                }
                if (!formData.thumbnail) newErrors.thumbnail = 'Product image is required';
                if (!formData.maincategory) newErrors.maincategory = 'Main category is required';
                if (!formData.subcategory) newErrors.subcategory = 'Sub category is required';
            } else if (step === 1) {
                if (!isService && deliveryType.length === 0) newErrors.deliveryType = 'At least one delivery option must be selected';
                if (isService && !formData.serviceMode) newErrors.serviceMode = 'Service mode is required';
                if (deliveryType.includes('pickup')) {
                    if (!formData.pickupLocation.country.name.trim()) newErrors.pickupCountry = 'Pickup country is required';
                    if (!formData.pickupLocation.state.name.trim()) newErrors.pickupState = 'Pickup state is required';
                    if (!formData.pickupLocation.city.trim()) newErrors.pickupCity = 'Pickup city is required';
                    if (!formData.pickupLocation.address.trim()) newErrors.pickupAddress = 'Pickup address is required';
                }
                if (deliveryType.includes('delivery') && !isService) {
                    if (formData.sellingScope === 'international') {
                        const pType = formData.shippingPolicy?.type || 'byOrder';
                        ['nearby', 'withinCountry', 'worldwide'].forEach(key => {
                            const r = formData.shippingPolicy?.regions?.[key] || {};
                            if (r.free) return;
                            if (pType === 'byOrder' && (r.flatFee === '' || r.flatFee === undefined)) {
                                newErrors[`shipping_${key}`] = `Shipping fee for ${key.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`;
                            } else if (pType === 'byUnit' && (r.perUnitFee === '' || r.perUnitFee === undefined)) {
                                newErrors[`shipping_${key}`] = `Per-unit fee for ${key.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`;
                            } else if (pType === 'byMOQ' && (r.moqFlatFee === '' || r.moqFlatFee === undefined || r.moqQty === '' || r.moqQty === undefined)) {
                                newErrors[`shipping_${key}`] = `MOQ shipping values for ${key.replace(/([A-Z])/g, ' $1').toLowerCase()} are required`;
                            }
                        });
                    } else if (!formData.shippingPolicy?.free) {
                        const pType = formData.shippingPolicy?.type || 'byOrder';
                        if (pType === 'byOrder' && (formData.shippingPolicy?.flatFee === '' || formData.shippingPolicy?.flatFee === undefined)) {
                            newErrors.shippingPolicy = 'Flat shipping fee is required';
                        } else if (pType === 'byUnit' && (formData.shippingPolicy?.perUnitFee === '' || formData.shippingPolicy?.perUnitFee === undefined)) {
                            newErrors.shippingPolicy = 'Per-unit shipping fee is required';
                        } else if (pType === 'byMOQ' && (formData.shippingPolicy?.moqFlatFee === '' || formData.shippingPolicy?.moqQty === '')) {
                            newErrors.shippingPolicy = 'MOQ shipping values are required';
                        }
                    }
                }
            } else if (step === 2) {
                const parseTime = (ts) => { if (!ts) return { number: '', unit: '' }; const p = ts.split(' '); return p.length === 2 ? { number: p[0], unit: p[1] } : { number: '', unit: '' }; };
                const isOnlineService = isService && formData.serviceMode === 'online';
                if (activeTab === 'variable') {
                    if (!formData.briefDescription.trim()) newErrors.briefDescription = 'Brief description is required';
                    if (!basePrice || parseFloat(basePrice) < 5) newErrors.basePrice = 'Base price must be at least USD 5';
                    if (!isOnlineService && deliveryType.includes('delivery')) {
                        const t = parseTime(formData.timeToDeliver);
                        if (!t.number || !t.unit) newErrors.timeToDeliver = 'Time/days prior to shipment is required';
                    }
                    if (!isOnlineService && deliveryType.includes('pickup')) {
                        const t = parseTime(formData.timeframe);
                        if (!t.number || !t.unit) newErrors.timeframe = 'Timeframe for order to be ready is required';
                    }
                } else {
                    const activePackages = packages.filter(pkg => enabledPackages.includes(pkg.name));
                    activePackages.forEach((pkg) => {
                        const index = packages.indexOf(pkg);
                        if (!pkg.subtitle.trim()) newErrors[`package_${index}_subtitle`] = 'Package name is required';
                        if (!pkg.briefDescription || pkg.briefDescription.length > 100) newErrors[`package_${index}_briefDescription`] = 'Brief description is required (max 100 chars)';
                        if (!pkg.options.basePrice || parseFloat(pkg.options.basePrice) < 5) newErrors[`package_${index}_basePrice`] = 'Price must be at least USD 5';
                        if (!isOnlineService && deliveryType.includes('delivery')) {
                            const t = parseTime(pkg.options.timeToDeliver);
                            if (!t.number || !t.unit) newErrors[`package_${index}_timeToDeliver`] = 'Required for delivery';
                        }
                        if (!isOnlineService && deliveryType.includes('pickup')) {
                            const t = parseTime(pkg.options.timeframe);
                            if (!t.number || !t.unit) newErrors[`package_${index}_timeframe`] = 'Required for pickup';
                        }
                    });
                }
            }
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            scrollToFirstStepError();
        }
        return Object.keys(newErrors).length === 0;
    }, [activeTab, basePrice, formData, deliveryType, packages, enabledPackages, isService, isB2B, priceTiers, customizationOptions, scrollToFirstStepError]);

    const handleNext = () => { if (validateStep(currentStep)) setCurrentStep(prev => Math.min(prev + 1, 2)); };
    const handlePrevious = () => setCurrentStep(prev => Math.max(prev - 1, 0));

    const handleB2BSubmit = async () => {
        const b2bData = {
            title: formData.productTitle,
            description: formData.productDescription,
            maincategory: formData.maincategory,
            subcategory: formData.subcategory,
            childcategory: formData.childcategory || '',
            listingType: formData.listingType || 'product',
            listingMode: formData.listingMode,
            moq: parseInt(formData.moq),
            sellingScope: formData.sellingScope || 'international',
            cancellable: formData.cancellable,
            productDetails: formData.productDetails.filter(d => d.title.trim() && d.description.trim()),
            featureTable: featureTable.filter(f => f.label?.trim() && f.value?.trim()),
            priceTiers: priceTiers.filter(t => t.minQty && t.price).map(t => ({
                minQty: parseInt(t.minQty), price: parseFloat(t.price),
            })),
            variations: b2bVariations,
            shippingPolicy: formData.shippingPolicy?.type ? formData.shippingPolicy : undefined,
            estimatedDeliveryDays: formData.sellingScope === 'international'
                ? {
                    nearby: formData.estimatedDeliveryDays?.nearby || '',
                    withinCountry: formData.estimatedDeliveryDays?.withinCountry || '',
                    worldwide: formData.estimatedDeliveryDays?.worldwide || '',
                } : undefined,
            shippingTimeDays: formData.shippingTimeDays ? parseInt(formData.shippingTimeDays) : undefined,
            offersCustomization: formData.listingMode === 'customization' || formData.listingMode === 'both',
            customizationOptions: customizationOptions.filter(o => o.description?.trim()).map(o => ({
                description: o.description,
                moq: o.moq ? parseInt(o.moq) : undefined,
                samplePrice: o.samplePrice ? parseFloat(o.samplePrice) : undefined,
                sampleShippingFee: o.sampleShippingFee ? parseFloat(o.sampleShippingFee) : undefined,
            })),
            saleDiscount: saleDiscount.value ? {
                type: saleDiscount.type, value: parseFloat(saleDiscount.value),
                endDate: saleDiscount.endDate || undefined,
            } : undefined,
            addedServices: addedServices.filter(s => s.name?.trim()).map(s => ({
                name: s.name, description: s.description || '', price: parseFloat(s.price) || 0,
                pricingMode: s.pricingMode || 'perOrder',
            })),
            buyerQuestions: buyerQuestions.filter(q => q.question?.trim()).map(q => ({ question: q.question.trim() })),
        };

        const fd = new FormData();
        Object.entries(b2bData).forEach(([key, value]) => {
            if (value === undefined) return;
            if (typeof value === 'object') fd.append(key, JSON.stringify(value));
            else fd.append(key, value);
        });

        if (isEditing) {
            let existingImages = [];
            formData.images.forEach(image => {
                if (image.file && image.file instanceof File) fd.append('images', image.file);
                else existingImages.push(image.url);
            });
            fd.append('existingImages', JSON.stringify(existingImages));
        } else {
            formData.images.forEach((imageFile) => { fd.append('images', imageFile.file); });
        }

        const token = localStorage.getItem('token');
        if (!token) { showToast("Please log in", "error"); navigate('/login'); return; }

        try {
            setIsLoading(true);
            const url = `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/seller/listings`;
            const headers = { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` };
            const response = isEditing
                ? await axios.put(`${url}/${id}`, fd, { headers })
                : await axios.post(url, fd, { headers });
            if (response.data.success) {
                showToast(isEditing ? "B2B listing updated!" : "B2B listing created!", "success");
                navigate('/postings');
            } else {
                showToast(isEditing ? "Failed to update listing" : "Failed to create listing", "error");
            }
        } catch (error) {
            console.error('Error submitting B2B listing:', error);
            showToast(error.response?.data?.message || 'An error occurred', "error");
        } finally { setIsLoading(false); }
    };

    const handleSubmit = async () => {
        if (!validateStep(currentStep)) return;
        if (isB2B) return handleB2BSubmit();

        const activePackagesList = activeTab === 'withoutVariable'
            ? packages.filter(pkg => enabledPackages.includes(pkg.name))
            : undefined;

        const productData = {
            productTitle: formData.productTitle,
            productDescription: formData.productDescription,
            ingredients: formData.maincategory === 'Food, Sweets & Drinks' ? formData.ingredients : undefined,
            productDetails: formData.maincategory !== 'Food, Sweets & Drinks' ? formData.productDetails : undefined,
            allergyInfo: formData.maincategory === 'Food, Sweets & Drinks' ? formData.allergyInfo : undefined,
            deliveryType: isService
                ? (formData.serviceMode === 'online' ? ['delivery'] : formData.serviceMode === 'onsite' ? ['delivery'] : ['pickup'])
                : deliveryType,
            pickupLocation: deliveryType.includes('pickup') ? formData.pickupLocation : undefined,
            cancellable: formData.cancellable,
            sellingScope: formData.sellingScope || 'local',
            shippingPolicy: formData.shippingPolicy?.type ? formData.shippingPolicy : undefined,
            estimatedDeliveryDays: formData.sellingScope === 'international' ? formData.estimatedDeliveryDays : undefined,
            serviceMode: isService ? formData.serviceMode : undefined,
            serviceArea: formData.serviceArea || 'country',
            serviceStates: formData.serviceStates || [],
            briefDescription: activeTab === 'variable' ? formData.briefDescription : undefined,
            basePrice: activeTab === 'variable' ? basePrice : undefined,
            timeToDeliver: activeTab === 'variable' ? formData.timeToDeliver : undefined,
            timeframe: activeTab === 'variable' ? formData.timeframe : undefined,
            variables: activeTab === 'variable' ? variables : undefined,
            packages: activePackagesList,
            discounts: discounts.length > 0 ? discounts : undefined,
            maincategory: formData.maincategory,
            subcategory: formData.subcategory,
            childcategory: formData.childcategory || '',
            stock: isService ? null : formData.stock,
            saleDiscount: saleDiscount.value ? {
                type: saleDiscount.type,
                value: parseFloat(saleDiscount.value),
                endDate: saleDiscount.endDate || undefined,
            } : undefined,
            bulkPricing: isService ? [] : bulkPricing.filter(t => t.minQuantity && t.price).map(t => ({
                minQuantity: parseInt(t.minQuantity),
                price: parseFloat(t.price),
                packageName: t.packageName || '',
            })),
            addedServices: addedServices.filter(s => s.name?.trim()).map(s => ({
                name: s.name,
                description: s.description || '',
                price: parseFloat(s.price) || 0,
                image: s.image || undefined,
                pricingMode: s.pricingMode || 'perOrder',
            })),
            buyerQuestions: buyerQuestions.filter(q => q.question?.trim()).map(q => ({
                question: q.question.trim(),
                image: q.image || '',
            })),
        };

        const formDataToSend = new FormData();
        formDataToSend.append('productData', JSON.stringify(productData));

        if (isEditing) {
            let existingImages = [];
            formData.images.forEach(image => {
                if (image.file && image.file instanceof File) formDataToSend.append("images", image.file);
                else existingImages.push(image.url);
            });
            formDataToSend.append("existingImages", JSON.stringify(existingImages));
        } else {
            formData.images.forEach((imageFile) => { formDataToSend.append('images', imageFile.file); });
        }

        const token = localStorage.getItem('token');
        if (!token) { showToast("Please log in to create or update a product", "error"); navigate('/login'); return; }

        try {
            setIsLoading(true);
            let response;
            if (isEditing) {
                response = await axios.put(
                    `${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/seller/product/${id}`,
                    formDataToSend,
                    { headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` } }
                );
            } else {
                response = await axios.post(
                    `${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/seller/product/new`,
                    formDataToSend,
                    { headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` } }
                );
            }
            if (response.data.success) {
                showToast(isEditing ? "Product updated successfully!" : "Product created successfully!", "success");
                navigate('/postings');
            } else {
                showToast(isEditing ? "Failed to update product" : "Failed to create product", "error");
            }
        } catch (error) {
            console.error('Error submitting product:', error);
            showToast(error.response?.data?.message || 'An error occurred while submitting the product', "error");
        } finally { setIsLoading(false); }
    };

    return (
        <div className="cpPage">
            {isLoading && <Loader type='checkmate' />}
            <section className="cpMainSection">
                <div className="cpPageHeader">
                    <h1 className="cpPageTitle">
                        {isEditing
                            ? (isB2B ? 'Edit B2B Listing' : 'Edit Product')
                            : (isB2B ? 'Create B2B Listing' : 'Create New Listing')}
                    </h1>
                    <p className="cpPageSubtitle">
                        {isEditing
                            ? (isB2B ? 'Update your B2B listing details' : 'Update your product details and settings')
                            : (isB2B ? 'Fill in the details to list on the B2B marketplace' : 'Fill in the details below to list your product or service')}
                    </p>
                </div>
                <StepIndicator currentStep={currentStep} labels={isB2B ? B2B_STEP_LABELS : STEP_LABELS} />

                <div className="cpStepContent">
                    {currentStep === 0 && (
                        isB2B ? (
                            <B2BDetailsStep
                                formData={formData} handleInputChange={handleInputChange} errors={errors}
                                setFormData={setFormData} b2bCategories={b2bCategories}
                                featureTable={featureTable} setFeatureTable={setFeatureTable}
                            />
                        ) : (
                            <ProductDetailsStep
                                formData={formData} handleInputChange={handleInputChange} errors={errors}
                                setFormData={setFormData} sellerCountry={sellerCountry} isService={isService}
                            />
                        )
                    )}
                    {currentStep === 1 && (
                        isB2B ? (
                            <B2BPricingStep
                                formData={formData} errors={errors}
                                priceTiers={priceTiers} setPriceTiers={setPriceTiers}
                                b2bVariations={b2bVariations} setB2bVariations={setB2bVariations}
                                customizationOptions={customizationOptions} setCustomizationOptions={setCustomizationOptions}
                                saleDiscount={saleDiscount} setSaleDiscount={setSaleDiscount}
                                addedServices={addedServices} setAddedServices={setAddedServices}
                                buyerQuestions={buyerQuestions} setBuyerQuestions={setBuyerQuestions}
                                currency={currency} exchangeRate={exchangeRate}
                                feesObj={feesObj}
                            />
                        ) : (
                            <DeliveryPoliciesStep
                                deliveryType={deliveryType} setDeliveryType={setDeliveryType}
                                formData={formData} setFormData={setFormData} errors={errors}
                                handleInputChange={handleInputChange}
                                sellerAddress={user?.sellerId?.sellerAddress}
                                accountZipCode={user?.buyerAddress?.zipCode}
                                isService={isService}
                                currency={currency} exchangeRate={exchangeRate}
                            />
                        )
                    )}
                    {currentStep === 2 && (
                        isB2B ? (
                            <B2BShippingExtrasStep
                                formData={formData} setFormData={setFormData} handleInputChange={handleInputChange}
                                errors={errors}
                                currency={currency} exchangeRate={exchangeRate}
                            />
                        ) : (
                            <PricingServicesStep
                                basePrice={basePrice} setBasePrice={setBasePrice}
                                variables={variables} setVariables={setVariables}
                                presetVariables={presetVariables}
                                formData={formData} setFormData={setFormData}
                                handleInputChange={handleInputChange}
                                activeTab={activeTab} setActiveTab={setActiveTab}
                                errors={errors} deliveryType={deliveryType}
                                packages={packages} setPackages={setPackages}
                                enabledPackages={enabledPackages} setEnabledPackages={setEnabledPackages}
                                isEditing={isEditing}
                                feesObj={feesObj} currency={currency} exchangeRate={exchangeRate}
                                addedServices={addedServices} setAddedServices={setAddedServices}
                                saleDiscount={saleDiscount} setSaleDiscount={setSaleDiscount}
                                bulkPricing={bulkPricing} setBulkPricing={setBulkPricing}
                                isService={isService}
                                buyerQuestions={buyerQuestions} setBuyerQuestions={setBuyerQuestions}
                            />
                        )
                    )}
                </div>

                <div className="cpNavButtons">
                    {currentStep > 0 && (
                        <button className="cpNavBtn cpNavBtnBack" onClick={handlePrevious}>
                            <ChevronLeft size={18} /> Back
                        </button>
                    )}
                    <div className="cpNavBtnSpacer" />
                    {currentStep < 2 ? (
                        <button className="cpNavBtn cpNavBtnNext" onClick={handleNext}>
                            Continue <ChevronRight size={18} />
                        </button>
                    ) : (
                        <button className="cpNavBtn cpNavBtnSubmit" onClick={handleSubmit}>
                            {isEditing
                                ? (isB2B ? 'Update Listing' : 'Update Product')
                                : (isB2B ? 'Publish Listing' : 'Publish Product')}
                        </button>
                    )}
                </div>
            </section>
        </div>
    );
};

const CreateProductPage = () => {
    const [searchParams] = useSearchParams();
    const isB2B = searchParams.get('market') === 'b2b';

    return <CreateProductPageForm isB2B={isB2B} />;
};

export default CreateProductPage;
